import * as d3 from "d3";
import * as d3Drag from "d3-drag";
import * as d3Force from "d3-force";
import * as d3Selection from "d3-selection";
import * as d3Zoom from "d3-zoom";
import { ICommoditySelector } from "../entities/select-commodity-options";
import { bootbox } from "../infrastructure/bootbox-shim";
import { CommodityAndUnitsRec } from "./ah.mb.commodity-and-units-rec";
import { LinkProperty } from "./ah.mb.model-utils";
import { ModelUtils } from "./ah.mb.ui.model-utils";
import { NetworkDiagramState } from "./ah.modelbuilder.network-diagram-state";
import { INodeDef, NodeDefs, SymbolShapeString } from "./ah.modelbuilder.node-defs";
import { ILinkingNode, ILinkObject, INodeObject, NodeObject } from "./common/ah.mb.common.node-object";
import { IModelActionsHandler } from "./model-notifier/ah.mb.common.model-actions-handler";
import { ICoordinate, IModelObserver, INodeDiagramPosition } from "./model-notifier/ah.mb.ui.model-observer";

interface INodeIcon extends d3Force.SimulationNodeDatum, INodeObject {
    fx?: number;
    fy?: number;
    x: number;
    y: number;

    splitName: Array<string>;
    isShadowNode: boolean;

    selectionTime: number;
}

// Debug elements
interface IKinkNodeIcon extends INodeIcon {
    link: ILinkControl;
}

interface ICaptionNodeIcon extends INodeIcon {
    node: INodeIcon;
    width: number;
    height: number;
}

interface ICaptionNodeLink extends ILinkControl {
    captionNode: ICaptionNodeIcon
}

export interface IOldNodeIcon extends INodeObject {
    fixed: boolean;
    x: number;
    y: number;
}

export enum NodeDistributionMode {
    vertically,
    horizontally,
    obliquely
}

export enum NodeDistributionModeDirection {
    forward,
    backward
}

interface ILinkControl extends d3Force.SimulationLinkDatum<INodeIcon>, ILinkObject {
    commodity: string;
    units: string;
    properties: string;
    source: INodeIcon;
    target: INodeIcon;
    kinkPoint: ICoordinate;
    id: string; // used for selection
}

interface SVGDatum {
    width: number;
    height: number;
}

interface INetworkDiagramContext {
    isDragging: boolean;
    hasDragged: boolean;
    nodeUnderMouse: INodeIcon;
    sectorPathAndName: string;
    doingPaletteDrag: boolean;
}

//interface INodeDragSubject {
//    isDragging: boolean;
//}

export class NetworkDiagramConfig {

    showPalette: boolean = true;
    centerDiagram: boolean = false;
    snapNodesToGrid: boolean = true;
    snapGridSize: number = 50;
    autoCreateKinkPoints: boolean = true;
    alwaysShowKinkPoints: boolean = true;
    forceSimulationConfig: DiagramForceSimulationConfig = DiagramForceSimulationConfig.Default;
    applyKinkPointsToLinks: boolean = true;

}

export class DiagramForceSimulationConfig {
    createFalseNodesForSimulation: boolean = true;
    renderFalseNodes: boolean = false;
    autoRelax: boolean = false;

    static Default: DiagramForceSimulationConfig =
        {
            autoRelax: false,
            createFalseNodesForSimulation: true,
            renderFalseNodes: false
        }
}

export class NetworkDiagramBuilder implements IModelObserver {

    // Private vars
    public svg: d3Selection.Selection<SVGSVGElement, SVGDatum, any, any>;
    private container: d3Selection.Selection<any, any, any, any>;
    private zoom: d3Zoom.ZoomBehavior<SVGSVGElement, any>;
    private width = 0;
    private height = 0;
    private readonly commoditySelector: ICommoditySelector;
    private selectedNode: INodeIcon;
    private dragLine: d3Selection.Selection<SVGLineElement, any, SVGSVGElement, any>;
    private diagramState: NetworkDiagramState;
    private force: d3Force.Simulation<d3.SimulationNodeDatum, ILinkControl>;
    private showPaletteNodeCaptions: boolean;
    public buildConfig: NetworkDiagramConfig;

    private drag: d3.DragBehavior<SVGElement, INodeIcon, any>;
    private handleKeyboardEvents: boolean;
    private readonly getDimensionsFunc: (selecterStr) => { width: number, height: number };

    private readonly nodeLinkClickHandler: (nodeId: string, boolean) => void;
    private readonly modelValidation: ModelUtils;

    private linkingNode: ILinkingNode;// mb.common.NodeRecord;

    private links: Array<ILinkControl>;
    private curve: d3.Line<ICoordinate>;
    private kinkDrag: d3.DragBehavior<SVGElement, ILinkControl, ILinkControl>;

    nodes: Array<INodeIcon>;

    // For detecting double-click v single click
    private clickedOnce = false;
    private clickTimer = null;

    private selectedLink: ILinkControl = null;
    private mousedownLink = null;

    //private curScale: number;
    //private curScroll: [number, number];

    private selecting = false;

    private readonly showLinkDataHandler: (sourceNodeId: string, destNodeId: string) => void;

    private paletteOrigX = 0;
    private paletteOrigY = 0;

    // private linkHintDiv: d3.Selection<any>;


    private targetDivName: string;
    private targetDivPrefix: string;

    private readonly editNodeDataHandler: (nodeId: string) => void;

    private readonly nodeDefs: NodeDefs;

    private readonly popupContainer: JQuery;

    //   private paletteText: d3Selection.Selection<SVGTextElement, INodeDef, any, any>;
    //    private node: d3Selection.Selection<SVGSymbolElement, INodeIcon, any, any>;
    //    private link: d3Selection.Selection<SVGLineElement, ILinkControl, any, any>;
    //    private text: d3Selection.Selection<SVGTextElement, INodeIcon, any, any>;
    //    private palette: d3.Selection<SVGSymbolElement, INodeDef, any, any>;

    private readonly modelActionsHandler: IModelActionsHandler;

    private initialNodeName: string;

    private lastCreatedNodeViaPalette: string = "";

    private readonly nodeSelectedHandler: (node: INodeObject | INodeObject[]) => void;
    //        private readonly selectionChangedHandler: (node: INodeObject[]) => void;

    private context: INetworkDiagramContext;

    shiftZoomed(this: NetworkDiagramBuilder) {
        const sourceEvent = (d3.event as d3.BaseEvent);
        var mouseEvent = sourceEvent.sourceEvent as MouseEvent;
        if (mouseEvent && mouseEvent.shiftKey)
            this.svgMouseMove();

    }

    // Diagram this.zoomed event handler
    zoomed(this: NetworkDiagramBuilder) {

        {
            // Only if no selection rect:
            const sourceEvent = (d3.event as d3.BaseEvent);
            var mouseEvent = sourceEvent.sourceEvent as MouseEvent;
            if (!sourceEvent.sourceEvent || !(mouseEvent.shiftKey)) {

                const zoomEvent = (d3.event as d3Zoom.D3ZoomEvent<SVGSVGElement, any>);

                if (zoomEvent && !isNaN(zoomEvent.transform.x)) {
                    var svg = d3
                        .select<SVGSVGElement, undefined>(`${this.targetDivName}`);

                    var container = svg.select("g");


                    // d3 v3:
                    // `translate(${zoomEvent.translate})scale(${zoomEvent.scale})`);
                    var x = zoomEvent.transform.x;
                    var y = zoomEvent.transform.y;
                    var scale = zoomEvent.transform.k;

                    container.attr("transform",
                        `translate(${[x, y]})scale(${scale})`);
                    // Save scale for later saving to db
                    // Todo fix

                    this.diagramState.setState(x, y, scale);


                }
            }
        }


        if (mouseEvent && mouseEvent.shiftKey)
            this.svgMouseMove();


        //else
        // Alan this messes up the pan
        //                        if (keybdEvent.shiftKey) {
        //       this.svgMouseDown();
        // }
        //.on("start", () => this.svgMouseDown())
        //        .on("end", () => this.svgMouseUp());

        //  }

    }

    //pathTransform(d: ILinkControl) {


    //    var src = d.source as INodeIcon;
    //    var target = d.target as INodeIcon;
    //    var dr = 0;
    //    return `M${src.x},${src.y}A${dr},${dr} 0 0,1 ${target.x},${target.y}`;
    //}

    private nodeTransform(d: INodeIcon | INodeDef) {
        return `translate(${d.x},${d.y})`;
    }

    private nodeTransformAt(x, y: number) {
        return `translate(${x},${y})`;
    }

    tick() {
        // Diagram elements
        this.nodeIconsTick(this.getNodeIcons());
        this.linkControlsTick(this.getLinkControls());

        this.nodeCaptionsTick(this.getNodeCaptionElements());
        this.kinkPointElementsTick(this.getKinkPointElements());

        this.paletteIconsTick(this.getPaletteIcons());
        this.paletteLabelsTick(this.getPaletteLabels());

        if (this.buildConfig.forceSimulationConfig.renderFalseNodes)
            this.tickFalseElements();
    };

    private getPaletteIcons(): d3.Selection<SVGSymbolElement, INodeDef, SVGGElement, any> {
        return this.svg.selectAll(".palette-icon");
    }

    private paletteIconsTick(selection: d3.Selection<SVGSymbolElement, INodeDef, any, any>):
        d3.Selection<SVGSymbolElement, INodeDef, any, any> {
        return selection.attr("transform", this.nodeTransform);
    }


    private getPaletteLabels(): d3.Selection<SVGTextElement, INodeDef, SVGGElement, any> {
        return this.svg.selectAll(".paletteText");

    }
    private paletteLabelsTick(selection: d3.Selection<SVGTextElement, INodeDef, SVGGElement, any>):
        d3.Selection<SVGTextElement, INodeDef, SVGGElement, any> {
        return selection.attr("transform", this.nodeTransform);

    }

    private getLinkControls(): d3.Selection<SVGLineElement, ILinkControl, any, any> {
        return this.container.selectAll<SVGLineElement, ILinkControl>(".link");
    }

    private linkControlsTick(selection: d3.Selection<SVGLineElement, ILinkControl, any, any>): d3.Selection<SVGLineElement, ILinkControl, any, any> {

        return selection.attr("d", d => this.linkTransform(d)); //  this.pathTransform);
    }

    private getNodeIcons(): d3.Selection<SVGSymbolElement, INodeIcon, SVGGElement, any> {
        return this.container.selectAll<SVGSymbolElement, INodeIcon>(".node-icon");

    }

    private nodeIconsTick(selection: d3.Selection<SVGSymbolElement, INodeIcon, any, any>): d3.Selection<SVGSymbolElement, INodeIcon, SVGGElement, any> {
        return selection.attr("transform", this.nodeTransform);
    }

    private getNodeCaptionElements(): d3.Selection<SVGTextElement, INodeIcon, SVGGElement, any> {
        return this.container.selectAll<SVGTextElement, INodeIcon>(".node-caption");
    }

    private nodeCaptionsTick(selection: d3.Selection<SVGTextElement, INodeIcon, SVGGElement, any>): d3.Selection<SVGTextElement, INodeIcon, SVGGElement, any> {
        return selection.attr("transform", this.nodeTransform);

    }

    private getKinkPointElements(): d3.Selection<SVGSymbolElement, ILinkControl, SVGGElement, any> {
        return this.container.selectAll<SVGSymbolElement, ILinkControl>(".kinkpoint");

    }
    private kinkPointElementsTick(selection: d3.Selection<SVGSymbolElement, ILinkControl, SVGGElement, any>): d3.Selection<SVGSymbolElement, ILinkControl, SVGGElement, any> {
        return selection.attr("transform", d => this.getKinkTransform(d));
    }

    private getKinkTransform(d: ILinkControl): string {
        const kinkPoint = this.normalizeKinkPoint(d.source, d.target, d.kinkPoint);
        return `translate(${d.source.x + kinkPoint.x},${d.source.y + kinkPoint.y})`;
    }


    tickFalseElements() {

        // Kink node icons
        const kinkIconElements = this.svg.selectAll<SVGCircleElement, IKinkNodeIcon>(".kink-node");
        // console.log(`${kinkIconElements.data().length} kink nodes`);
        kinkIconElements.attr("transform", d => {

            const kinkPoint = this.normalizeKinkPoint(d.link.source, d.link.target, d.link.kinkPoint);
            //{ d.x: d.x, y: d.y }
            const trans =
                `translate(${d.link.source.x + kinkPoint.x},${d.link.source.y + kinkPoint.y})`;
            return trans;

        });

        // Caption node elements
        const captionIconElements = this.svg.selectAll<SVGRectElement, ICaptionNodeIcon>(".caption-node");
        // console.log(`${captionIconElements.data().length} caption link icons`);
        captionIconElements.attr("transform", d => `translate(${d.node.x + 22},${d.node.y - 10})`);

        // Caption links
        const captionlinkElements = this.svg.selectAll<SVGLineElement, ICaptionNodeLink>(".caption-link-line");
        // console.log(`${captionlinkElements.data().length} caption link lines`);
        captionlinkElements.attr("d", d => this.directLinkTransform(
            { x: d.captionNode.node.x, y: d.captionNode.node.y },
            { x: d.captionNode.node.x + 22, y: d.captionNode.node.y - 10 }));


    }


    // Node drag start event handler
    dragstarted(d: INodeIcon, context: INetworkDiagramContext) {
        //const e = event as d3Drag.D3DragEvent<SVGCircleElement, INodeIcon, INodeIcon | d3Drag.SubjectPosition>;

        //var d = this;

        // This is the node being dragged
        var beingDragged =
            d3.selectAll(".node-icon")
                .filter((icon: INodeIcon) => icon.name === d.name);

        // Note: following is not needed really
        // Have to figure out if this is a new drag, or a drag of selected icons
        // Find out if dragged node is in selection
        var currentSelection = d3.selectAll(".node-icon.selected");
        var crossMatch = currentSelection.filter((icon: INodeIcon) => icon.name === d.name);
        // If node being dragged is not in list, it is a new drag
        // Or, a node already being selected is being dragged again
        // var isInGroupSelection = currentSelection.size() > 1 && crossMatch.size() === 1;
        // if (!isInGroupSelection)
        {

            // Select the new node
            beingDragged.classed("selected", true);
            d.selectionTime = new Date().getTime();
            //beingDragged.attr("selectionTime", )

            // If the node clicked was not previously selected, this
            // must be a new selection, so deselect others
            // unless it's a shift-click

            const baseEvent = d3.event as d3.BaseEvent;
            const keybdEvent = (baseEvent.sourceEvent as KeyboardEvent);
            if (!keybdEvent.shiftKey)
                if (crossMatch.size() === 0)
                    d3.selectAll(".node-icon")
                        .filter((icon: INodeIcon) => icon.name !== d.name)
                        .classed("selected", false);

            context.isDragging = !keybdEvent.shiftKey;
            context.hasDragged = false;
            this.force.stop();
            baseEvent.sourceEvent.stopPropagation();
        }
    }

    // Dragged event handler (existing node or link)
    dragged(d: INodeIcon, context: INetworkDiagramContext) {

        const dragEvent = (d3.event as d3Drag.D3DragEvent<SVGSymbolElement, INodeIcon, INodeIcon | d3Drag.SubjectPosition>); // d3Drag.D3DragEvent);

        if (context.isDragging) {


            // Todo upgrade this

            if (dragEvent.dx !== 0 || dragEvent.dy !== 0) {
                context.hasDragged = true;

                const selectedIcons = d3.selectAll<SVGSymbolElement, INodeIcon>(".node-icon.selected");

                // Multi-select drag
                selectedIcons.each((curNode: INodeIcon) => {

                    curNode.x += dragEvent.dx;
                    curNode.y += dragEvent.dy;

                    // Update kink points
                    const links = this.getNodeLinks(curNode);
                    links.forEach(link => {
                        if (NetworkDiagramBuilder.isValidCoordinate(link.kinkPoint)) {

                            // If only one end of the link is selected, we need to shift the kinkpoint
                            // Because the kinkpoint is stored relative to the source, we have to compensate 
                            // for the movement only if the source is moved
                            const srcNode = selectedIcons.filter(node => node.pathAndName == link.sourcePathAndName);
                            const destNode = selectedIcons.filter(node => node.pathAndName == link.destPathAndName);
                            if (!srcNode.empty() && destNode.empty()) {
                                link.kinkPoint.x = link.kinkPoint.x - dragEvent.dx;
                                link.kinkPoint.y = link.kinkPoint.y - dragEvent.dy;
                            }
                        }
                    });
                });

                // Restart layout
                //                var selectedNodeIcons = selectedIcons.data();
                //              var nodesToRelax = this.nodes.filter(p => selectedNodeIcons.indexOf(p) === -1);
                //            this.relaxLayout(/*selectedIcons.data().concat*/nodesToRelax);


                this.tick();
            }


        } else {

            this.dragLine
                .classed("hidden", false);

            // update drag line
            //dragLine.attr('d', 'M' + d.px + ',' + d.py + 'L' + d3.event.x + ',' + d3.event.y);
            this.dragLine.attr("x1", d.x);
            this.dragLine.attr("y1", d.y);
            this.dragLine.attr("x2", (dragEvent /*d3.event as DragEvent)*/.x));
            this.dragLine.attr("y2", (dragEvent /*d3.event as DragEvent)*/.y));

            // Too fix
            this.tick();
        }
    }

    // Node drag end event handler
    dragended(d: INodeIcon, context: INetworkDiagramContext, builder: NetworkDiagramBuilder) {
        // Hide dragline
        this.dragLine
            .classed("hidden", true);
        //.style('marker-end', '');


        // This will get called even if just clicked on node so make sure
        // It was actually dragged
        const dragEvent = (d3.event as d3Drag.D3DragEvent<SVGSymbolElement, INodeIcon, INodeIcon | d3Drag.SubjectPosition>);
        if (dragEvent.subject.x === dragEvent.x && dragEvent.subject.y === dragEvent.y) {

            // Testing
            this.nodeMouseUp(context.nodeUnderMouse, context);


            return;
        }

        if (context.isDragging

            // Todo fix && this.hasDragged
        ) {

            // Multi-select drag

            var newPositions = new Array<INodeDiagramPosition>();
            const newKinkPoints = new Array<ILinkControl>();

            const selectedIcons = d3.selectAll<SVGSymbolElement, INodeIcon>(".node-icon.selected")

            let nodesToRelax: INodeIcon[] = [];

            selectedIcons.each(curNode => {

                let isModified = false;

                // Snap to grid
                if (builder.buildConfig.snapNodesToGrid) {
                    const nx = Math.round(curNode.x / builder.buildConfig.snapGridSize) * builder.buildConfig.snapGridSize;
                    const ny = Math.round(curNode.y / builder.buildConfig.snapGridSize) * builder.buildConfig.snapGridSize;

                    isModified = curNode.x !== nx || curNode.y !== ny;

                    if (isModified) {
                        curNode.x = nx;
                        curNode.y = ny;
                    }

                }

                // Set the node to fixed so the force doesn't include the node in its auto positioning stuff
                builder.fixNode(curNode);

                // Todo fix 
                var canMove = builder.modelValidation.canRepositionNode(curNode);

                // If not can move (it  a shadow), we allow the move, but don't trigger the event
                if (canMove.ok) {
                    newPositions.push({
                        nodePathAndName: builder.getDiagramNodePathAndName(curNode), // Note: retain shadow name
                        xPos: curNode.x,
                        yPos: curNode.y
                    });

                    // Save affected kink points
                    const links = this.getNodeLinks(curNode);
                    links.forEach(link => {

                        // We also want to relax connected nodes
                        // only do this if we are the only node connected
                        if (selectedIcons.data().indexOf(link.source) === -1) {

                            const otherNodeLinkes = this.getNodeLinks(link.source);
                            if (otherNodeLinkes.length === 1)
                                nodesToRelax.push(link.source);

                        }
                        if (selectedIcons.data().indexOf(link.target) === -1) {

                            const otherNodeLinkes = this.getNodeLinks(link.target);
                            if (otherNodeLinkes.length === 1)
                                nodesToRelax.push(link.target);
                        }

                        if (NetworkDiagramBuilder.isValidCoordinate(link.kinkPoint)) {

                            // If only one end of the link is selected, we need to shift the kinkpoint
                            // Because the kinkpoint is stored relative to the source, we have to compensate 
                            // for the movement only if the source is moved
                            const srcNode = selectedIcons.filter(node => node.pathAndName == link.sourcePathAndName);
                            const destNode = selectedIcons.filter(node => node.pathAndName == link.destPathAndName);
                            if (!srcNode.empty() || !destNode.empty()) {
                                newKinkPoints.push(link);
                            }
                        }
                    });

                }

            });

            // Restart layout 
            if (this.buildConfig.forceSimulationConfig.autoRelax)
                this.relaxLayout(/*selectedIcons.data().concat*/(nodesToRelax));

            if (newPositions.length > 0 || newKinkPoints.length > 0) {
                // Indicate busy
                builder.saveNodePositions(newPositions, newKinkPoints);
            }

            builder.tick();
            if (context.hasDragged) {
                //builder.force.restart(); // was resume
            }

            context.isDragging = false;
            context.hasDragged = false;

            //const selection = d3.select(d3.event.sourceEvent.currentTarget);
            // To fix selection.classed("dragging", false);
        } else {
            const baseEvent = (d3.event as d3.BaseEvent);

            if (context.nodeUnderMouse && (baseEvent.sourceEvent as KeyboardEvent).shiftKey
                && d !== context.nodeUnderMouse) {
                // Create a link
                const srcCommodities = builder.getNodeCommodities(d);
                const destCommodities = builder.getNodeCommodities(context.nodeUnderMouse);
                const commodities =
                    builder.modelValidation.getCommodityUnion(srcCommodities,
                        destCommodities,
                        d.nodeType,
                        context.nodeUnderMouse.nodeType);

                const srcOutputs = builder.getNodeOutputLinks(d);
                const destInputs = builder.getNodeInputLinks(context.nodeUnderMouse);

                builder.addLink(d.name,
                    context.nodeUnderMouse.name,
                    context.sectorPathAndName,
                    context.sectorPathAndName,
                    commodities,
                    d.nodeType,
                    context.nodeUnderMouse.nodeType,
                    srcCommodities,
                    destCommodities,
                    srcOutputs,
                    destInputs);
            }
            builder.tick();
        }
    }

    // Diagram MouseMove handler
    mouseMove() {
        if (!this.selectedNode) return;
    }


    nodeMouseOver(d: INodeIcon, context: INetworkDiagramContext) {
        context.nodeUnderMouse = d;

    }

    nodeMouseOut(d: INodeIcon, context: INetworkDiagramContext) {
        context.nodeUnderMouse = null;
    }


    // Node mouse down
    nodeMouseDown(d: INodeIcon) {
        // Sets selected node

        if (!(d3.event as Event).defaultPrevented)
        //if (!isDragging && !hasDragged) {
        {
            // Check if selecting a new node
            // If so clear others selected

            let selection = d3.select(d3.event.currentTarget);
            var keybdEvent = event as KeyboardEvent;
            // If ctrl-click, toggle this selected 
            if (keybdEvent.ctrlKey) {
                selection.classed("selected", !selection.classed("selected"));

            } else if (selection.classed("selected")) {

                // Logic to stop the node selected in dragStarted being unselected
                // on a click
                // Not sure if the is needed
                const currentSelection = d3.selectAll(".node-icon.selected");

                const selectedNodeIsClicked =
                    currentSelection.filter((sel: INodeIcon) => sel.name === d.name);

                if (!keybdEvent.shiftKey)
                    if (currentSelection.size() > 1 || selectedNodeIsClicked.size() === 0) {
                        currentSelection.filter((icon: INodeIcon) => icon.name !== d.name).classed("selected", false);
                    }
            }
            else
                if (keybdEvent.shiftKey) {
                    selection.classed("selected", true);
                }

                else {
                    d3.select(".node-icon.selected").classed("selected", false);
                    selection.classed("selected", true);
                }

        }
        d3.event.stopPropagation();
    }

    /**
     * Link double-click handler
     * @param {} selLink 
     * @returns {} 
     */
    linkDoubleClick(selLink: ILinkControl) {
        this.showLinkDataHandler(selLink.sourcePathAndName, selLink.destPathAndName);
    }

    /**
     * Node caption click
     */
    nodeCaptionClick(theNode: INodeIcon) {
        // If shadow - trigger hyperlink
        if (theNode.isShadowNode) {
            if (this.nodeLinkClickHandler)
                this.nodeLinkClickHandler(this.getNodePathAndNameFromId(
                    theNode.pathAndName),
                    true);
        }
    }


    // Creates html valid id for node path and name
    getIdFromNodePathAndName(nodePathAndName: string): string {
        var res1 = nodePathAndName.split(/\\/).join("ZzZ"); // replace(/\\/, "ZzZ");
        // Hyphens are a problem as well - what to do?
        //var res2 = res1.replace(/ /g, "-");
        return res1;

    }

    // Gets node path and name from id
    getNodePathAndNameFromId(id: string): string {
        var res1 = id.split("ZzZ").join("\\"); //   id.replace("ZzZ", /\\/);


        // Hyphens are a problem as well - what to do?
        //var res2 = id.replace(/-/g, " ");
        return res1;
    }

    /**m
     * Get palette node id from it's name
     */
    getPaletteElementIdFromName(name: string) {
        return `palette_${name}`;
    }

    linkMouseDown(d: ILinkControl) {

        var dragTarget = d3.select("#link_" + d.id);
        dragTarget.classed("sel", !(dragTarget.classed("sel")));

        if ((d3.event as KeyboardEvent).ctrlKey) return;
        // select link
        this.mousedownLink = d;
        if (this.mousedownLink === this.selectedLink)
            this.selectLink(null);
        else
            this.selectLink(this.mousedownLink);

        this.selectNode(null);
        this.deselectAllNodes();

        //this.reDraw();

        d3.event.stopPropagation();
    }

    // Gets rid of links for a deleted node
    spliceLinksForNode(deletedNode: INodeIcon) {
        var toSplice = this.links.filter(l => l.source === deletedNode || l.target === deletedNode);
        toSplice.map(l => {
            this.links.splice(this.links.indexOf(l), 1);
        });
    }

    // Returns commodities associated with a node, via its links
    getNodeCommodities(theNode: INodeIcon): CommodityAndUnitsRec[] {
        var result = [];
        var linksOfNode = this.links.filter(l => l.source === theNode || l.target === theNode);

        linksOfNode.map(l => {

            // Make sure no duplicates
            var bfound = false;

            for (let j = 0; j < result.length; j++)
                if (result[j].commodity === l.commodity)
                    bfound = true;

            if (!bfound)
                result.push(new CommodityAndUnitsRec(l.commodity, l.units));
        });

        return result;
    }


    // Palette node drag start event
    paletteDragstarted(d: INodeDef, builder: NetworkDiagramBuilder) {

        //if (!this.showPaletteNodeCaptions) {
        //    // Hide the link tooltip
        //    this.linkHintDiv
        //        .transition()
        //        .delay(0)
        //        .duration(0)
        //        .style("opacity", 0)
        //        .style("pointer-events", "none");
        //}

        builder.selectNode(null);
        builder.selectLink(null);

        builder.paletteOrigX = d.x;
        builder.paletteOrigY = d.y;

        // Deselect all nodes
        d3.selectAll(".node-icon")
            .classed("selected", false);

        var baseEvent = (d3.event as d3.BaseEvent);
        baseEvent.sourceEvent.stopPropagation();
        d3.select(baseEvent.sourceEvent.currentTarget).classed("dragging", true);
        d3.select("body").style("cursor", "move");
    }

    // Node mouse up handler
    // This is used to select a node when clicked
    // We do not use mouse down because that would affect dragging
    nodeMouseUp(d: INodeIcon, context: INetworkDiagramContext) {

        if (!context.doingPaletteDrag) {

            // Check if user is linking cross-region
            if (this.linkingNode.pathAndName) {

                const srcOutputs = this.linkingNode.outputLinks;
                const destInputs = this.getNodeInputLinks(d);

                const srcCommodities = this.linkingNode.commodities;
                const destCommodities = this.getNodeCommodities(d);

                const allCommodities = this.modelValidation.getCommodityUnion(srcCommodities,
                    destCommodities,
                    this.linkingNode.nodeType,
                    d.nodeType);

                this.addLink(this.linkingNode.name,
                    d.name,
                    this.linkingNode.path,
                    context.sectorPathAndName,
                    allCommodities,
                    this.linkingNode.nodeType,
                    d.nodeType,
                    srcCommodities,
                    destCommodities,
                    srcOutputs,
                    destInputs);
                this.linkingNode.pathAndName = "";
                this.selectNode(d);
            } else {

                if (this.clickedOnce) {
                    this.nodeDoubleClick(d);
                    this.clickedOnce = false;
                    clearTimeout(this.clickTimer);

                    // Stop the node being deselected in the click handler
                } else {

                    this.clickTimer = setTimeout(() => {

                        if (d === this.selectedNode)
                            this.selectNode(null);
                        else
                            this.selectNode(d);
                        this.clickedOnce = false;
                    },
                        150);
                    this.clickedOnce = true;
                }
            }
        }
    }

    // Node double-click handler
    nodeDoubleClick(selNode: INodeIcon) {
        this.editNodeData(selNode);
    }

    paletteDragged(d: INodeDef, context: INetworkDiagramContext, builder: NetworkDiagramBuilder) {
        context.doingPaletteDrag = true;
        var dragEvent = (d3.event as d3Drag.D3DragEvent<SVGSymbolElement, INodeDef, INodeDef | d3Drag.SubjectPosition>); // d3Drag.D3DragEvent);

        d.x += dragEvent.dx;
        d.y += dragEvent.dy;

        this.tick();
    }

    // Palette node end drag event
    paletteDragended(d: INodeDef, context: INetworkDiagramContext) {
        if (context.doingPaletteDrag) {
            d3.select("body").style("cursor", "default");

            const selector = this.getPaletteElementIdFromName(d.name);

            d3.select(selector).classed("dragging", false);

            // Need to "undo" scale and this.zoom on node because transform will do it                 
            const tempScale = this.diagramState.scale;
            //this.zoom.scale; d3.zoomTransform(svg).k; // this.zoom.transform.scale();
            const x = (d.x - this.diagramState.xPos) / tempScale;
            const y = (d.y - this.diagramState.yPos) / tempScale;

            const newName = this.getUniqueNodeName(`New ${d.name}`);

            // Move the dragged node back to the palette
            // Fix this to not move the original node but make a copy at the start of the drag, or it looks silly

            // Move the palette icon back
            var paletteIcon =
                d3.select(selector);
            paletteIcon
                .attr("x", d.x = this.paletteOrigX)
                .attr("y", d.y = this.paletteOrigY);

            this.paletteLabelsTick(this.getPaletteLabels());
            context.doingPaletteDrag = false;

            this.showInlineNodeNameEditorAt(x, y, newName, finalNewName => {

                // Save path of the new node, so when it shows up eventually
                // via nodeCreated we know not to scroll to it
                this.lastCreatedNodeViaPalette = finalNewName;

                // Call createNode of modelActionsHandler which will
                // ultimately come back into nodeCreated, if all went well

                this.modelActionsHandler.createNode(
                    context.sectorPathAndName,
                    finalNewName,
                    d.name,
                    {
                        XPos: x,
                        YPos: y
                    });

                // Move the palette icon back
                this.paletteIconsTick(this.getPaletteIcons());

            },
                // Move the palette icon back
                () => this.paletteIconsTick(this.getPaletteIcons()));

        }
    }

    // Gets a unique node name, to prevent duplications
    getUniqueNodeName = (origName: string): string => {
        var curSuffix = 0;
        var curNewName = origName;
        while (this.findNode(curNewName)) {
            curSuffix++;
            curNewName = origName + " " + curSuffix;
        }
        return curNewName;
    }

    /**
     * Creates a new icon and adds it to our internal list
     */
    addNodeIcon(pathAndName: string,
        name: string,
        nodeType: string,
        x: number,
        y: number): INodeIcon {

        const isFixed = x !== 0 || y !== 0;
        const isShadowNode = NodeObject.isShadowNodeName(name)

        var newNode: INodeIcon =
        {
            nodeType: nodeType,
            name: name,
            x: x,
            y: y,
            //px: x,
            //py: y,
            //fixed: isFixed,
            fx: isFixed ? x : null,
            fy: isFixed ? y : null,
            //fixed: isFixed,
            pathAndName: pathAndName,
            path: NodeObject.extractPath(pathAndName),
            //color: "red", // This should be passed in ?
            //shape: "circle", // This should be passed in ?,
            isFolder: false,
            fqmId: "",
            splitName: this.getSplitNodeName(name, isShadowNode),
            isShadowNode: isShadowNode,
            selectionTime: 0
        };
        this.nodes.push(newNode);

        return newNode;
    }

    getSplitNodeName(name: string, isShadow: boolean): Array<string> {

        if (isShadow) {
            const shadowEndPos = name.indexOf("]");
            const shadowSector = name.substring(0, shadowEndPos + 1);
            const end = name.substring(shadowEndPos + 2);
            const endStrs = this.splitString(4, end);
            const result = [shadowSector].concat(endStrs);
            return result;

        }

        return this.splitString(4, name);
    }

    splitString(maxWords: number, value: string): Array<string> {
        var app = value.split(' '),
            arrayApp = [],
            stringApp = "";
        app.forEach(function (sentence, index) {
            stringApp += sentence + ' ';

            if ((index + 1) % maxWords === 0) {
                arrayApp.push(stringApp);
                stringApp = '';
            } else if (app.length === index + 1 && stringApp !== '') {
                arrayApp.push(stringApp);
                stringApp = '';
            }
        });
        return arrayApp;

    };

    // Creates a new link, and re-draws
    addLink(source: string,
        target: string,
        srcPath: string,
        destPath: string,
        commodities: CommodityAndUnitsRec[],
        srcType: string,
        destType: string,
        existingSrcCommodities: CommodityAndUnitsRec[],
        existingDestCommodities: CommodityAndUnitsRec[],
        srcOutputs: ILinkObject[],
        destInputs: ILinkObject[]) {

        // Figure out required commodity
        this.commoditySelector.selectCommodity(commodities,
            "",
            (commodity, linkProperties) => {
                if (!commodity)
                    return;

                // Normalize properties string into an array
                var props = LinkProperty.propertiesFromString(linkProperties);

                if (srcPath !== destPath) {

                    // For a cross-region link, we can do limited validation
                    // Because we do not (currently) have the links associated with the 
                    // node in the other region

                    const canShadowLink = this.modelValidation.canLinkNodeTypes(srcType,
                        destType,
                        existingSrcCommodities,
                        existingDestCommodities,
                        commodity,
                        srcOutputs,
                        destInputs);

                    if (canShadowLink.ok) {
                        // Buffer command

                        this.modelActionsHandler.createLink(
                            srcPath + "\\" + source,
                            destPath + "\\" + target,
                            commodity.commodity,
                            commodity.units,
                            props
                        );


                    } else
                        bootbox.alert(canShadowLink.msg, () => { });

                } else {

                    // This is for an internal link which we can handle
                    const srcNode = this.findNode(source);
                    const destNode = this.findNode(target);

                    const srcOutputs = this.getNodeOutputLinks(srcNode);
                    const destInputs = this.getNodeInputLinks(destNode);

                    const canLink = this.modelValidation.canLink(srcNode,
                        destNode,
                        srcOutputs,
                        destInputs,
                        existingSrcCommodities,
                        existingDestCommodities,
                        commodity);

                    if (canLink.ok) {

                        this.modelActionsHandler.createLink(
                            srcNode.pathAndName,
                            destNode.pathAndName,
                            commodity.commodity,
                            commodity.units,
                            props
                        );

                        //                        this.reDraw();
                        //                        this.startSimulation(false);
                        //                      this.tick();

                    } else {
                        // Tell the user why link is invalid
                        bootbox.alert(canLink.msg, () => { });
                    }
                }
            });
    }

    // Rename node function
    renameNode(theNode: INodeIcon) {
        var canRename = this.modelValidation.canRename(theNode.nodeType,
            theNode.isShadowNode);
        if (canRename.ok) {

            // Call handler to rename the node
            // The handler will actually prompt for the node name, so we pass in
            // an empty string
            this.modelActionsHandler.renameNode(theNode.pathAndName);

        } else {
            bootbox.alert(canRename.msg, () => { });
        }
    };

    // Create link function - for cross - region links
    linkNode = (theNode: INodeIcon) => {
        // var commodities = this.getNodeCommodities(theNode);

        var allCommodities =
            this.getNodeCommodities(theNode).map(
                value => {
                    return { commodity: value.commodity, units: value.units };
                });

        // Copy to linking node
        // Don't assign linking node because it's a pointer that the owning page needs to maintain
        // for when another diagram is loaded. 
        this.linkingNode.name = theNode.name;
        this.linkingNode.path = theNode.path,
            this.linkingNode.isFolder = false,
            this.linkingNode.pathAndName = theNode.pathAndName,
            this.linkingNode.commodities = allCommodities,
            this.linkingNode.outputLinks = this.getNodeOutputLinks(theNode),
            this.linkingNode.nodeType = theNode.nodeType;

        this.showPopup(theNode);
    }

    // Deletes nodes and associated links
    removeNodeFromList(theNode: INodeIcon) {
        var nodeIndex = this.nodes.indexOf(theNode);
        this.nodes.splice(nodeIndex, 1);
        this.spliceLinksForNode(theNode);
    }

    // Deletes nodes and associated links
    removeNodesFromList(nodes: INodeIcon[]) {
        nodes.forEach(theNode => this.removeNodeFromList(theNode));
    }


    trimShadows() {

        // If shadows are orphaned by a delete, remove them 
        const orphans = this.nodes.filter(p => p.isShadowNode && this.getNodeLinks(p).length == 0);
        this.removeNodesFromList(orphans);

    }
    // Delete link function
    deleteLink(theLink: ILinkControl) {
        bootbox.confirm("Delete link?",
            result => {
                if (result)
                    this.modelActionsHandler.deleteLink(theLink.sourcePathAndName, theLink.destPathAndName);
                //source.pathAndName,
                //      theLink.target.pathAndName);
            });
    }

    // Edit link function
    editLink = (theLink: ILinkControl) => {
        var commodity = { commodity: theLink.commodity, units: theLink.units }
        var linkProperties = theLink.properties;
        this.commoditySelector.editCommodity(commodity,
            linkProperties,
            (result: CommodityAndUnitsRec, newProperties): void => {
                if (result) {

                    this.modelActionsHandler.editLink(
                        (theLink.source as INodeIcon).pathAndName,
                        (theLink.target as INodeIcon).pathAndName,
                        result.commodity,
                        result.units,
                        LinkProperty.propertiesFromString(newProperties));

                }
            });
    }

    // Edit node data function, called from node popup
    editNodeData(theNode: INodeIcon) {
        this.editNodeDataHandler(this.getNodePathAndNameFromId(theNode.pathAndName));
    }

    // Finds a node, by name
    // Todo: check if this works when shadow names are ambiguous
    findNode(name: string): INodeIcon {
        return this.nodes.find(p => p.name == name);
    }

    // Finds a node, by name
    // Todo: check if this works when shadow names are ambiguous
    findNodeIcon(name: string): d3.Selection<SVGSymbolElement, INodeIcon, any, any> {
        var icon = d3.select<SVGSymbolElement, INodeIcon>(".node-icon").filter((el, icon) => el.name == name);
        return icon;
    }

    // Gets all input and output links for a noe
    getNodeLinks = (theNode: INodeIcon): ILinkControl[] => {
        var result = this.links.filter(j => j.source === theNode || j.target === theNode);
        return result;
    }

    // Gets all output links of a node
    getNodeOutputLinks = (theNode: INodeIcon) => {
        var result =
            this.links.filter(j => j.source === theNode);
        return result;
    }

    // Gets all input links of a node
    getNodeInputLinks = (theNode: INodeIcon) => {
        var result = this.links.filter(j => j.target === theNode);
        return result;
    }

    linkTransform(d: ILinkControl): string {
        return this.curve(this.getLinkPoints(d.source, d.target, d.kinkPoint));
    }

    directLinkTransform(srcPos: ICoordinate, destPos: ICoordinate): string {
        return this.curve(this.getLinkPoints(srcPos, destPos, { x: 0, y: 0 }));
    }

    private redrawFalseNodes() {
        // Kink nodes
        const kinkNodes = this.getKinkNodes(this.nodes, this.links);

        const kinkNodeIconsFunc = () => this.container
            .selectAll<SVGCircleElement, INodeIcon>("g.kink-node")
            .data(kinkNodes);

        const kinkIcons = kinkNodeIconsFunc();

        var kinkIconsEnter = kinkIcons
            .enter()
            .append("g")
            .attr("class", "kink-node");

        kinkIconsEnter
            .append("path")
            .attr("d", d3.symbol().size(100).type(d3.symbolCircle))
            //            .attr("class", "kinknode-icon")
            .attr('pointer-events', 'none')
            .style("stroke-width", "3")
            .style("stroke", "aqua")
            .style("fill", "transparent") //  "#ccc");
            .classed("hidden", () => !this.buildConfig.forceSimulationConfig.renderFalseNodes);

        kinkIcons
            .exit()
            .remove();

        // Caption nodes
        const captionNodes = this.getCaptionNodes(this.nodes);

        const captionIconsFunc = () => this.container
            .selectAll<SVGRectElement, ICaptionNodeIcon>("g.caption-node")
            .data(captionNodes);

        var captionIcons = captionIconsFunc();

        var captionIconsEnter = captionIcons
            .enter()
            .append("g")
            .attr("class", "caption-node")

        captionIconsEnter
            .append("rect")

            .attr("width", d => d.width)
            .attr("height", d => d.height)

            //            .attr("class", "caption-node-icon")
            .attr('pointer-events', 'none')
            .style("stroke-width", "3")
            .style("stroke", "aqua")
            .style("fill", "transparent")
            .classed("hidden", () => !this.buildConfig.forceSimulationConfig.renderFalseNodes);


        captionIcons
            .exit()
            .remove();

        // Links to captions
        const captionLinks = this.getCaptionLinks(captionNodes);

        const captionLinkNodesFunc = () => this.container
            .selectAll<SVGLineElement, ICaptionNodeLink>("g.caption-link")
            .data(captionLinks);

        const captionLinkNodes = captionLinkNodesFunc();

        const captionLinkEnter = captionLinkNodes
            .enter()
            .append("g")
            .attr("class", "caption-link");

        captionLinkEnter
            //            .append("path")
            .insert("path", ".node")
            .attr("class", "caption-link-line")
            //            .attr("marker-end", `url(#${this.targetDivPrefix}_end)`)
            .style("stroke", "aqua")
            //            .attr("d", d => this.linkTransform(d))
            .attr('pointer-events', 'none')
            .classed("hidden", () => !this.buildConfig.forceSimulationConfig.renderFalseNodes);

        captionLinkNodes
            .exit()
            .remove();

    }

    // Main redraw function
    async reDraw(initialRedraw: boolean = false): Promise<any> {

        const elementOpacity = initialRedraw ? 1 : 0;

        if (this.buildConfig.forceSimulationConfig.createFalseNodesForSimulation) {
            this.redrawFalseNodes();
        }

        const link = this.container
            .selectAll<SVGLineElement, ILinkControl>("path.link")
            .data(this.links);

        const newLinks =
            link
                .enter()
                .insert("path", ".node")
                .attr("class", "link")
                .attr("marker-end", `url(#${this.targetDivPrefix}_end)`)
                .attr("d", d => this.linkTransform(d))
                .attr("id", d => `link_${d.id}`)
                .on("mousedown", link => this.linkMouseDown(link))
                .on("dblclick", link => this.linkDoubleClick(link))
                .on("mouseover", link => this.linkMouseOver(link))
                .on("mouseout", link => this.linkMouseOut(link))
                .style("opacity", elementOpacity); // Will be replaced by appear transition below


        let kinks: d3.Selection<SVGSymbolElement, ILinkControl, any, any>;
        let newKinks: d3.Selection<SVGPathElement, ILinkControl, any, any>;
        if (this.buildConfig.applyKinkPointsToLinks) {

            // Create kink controls
            kinks = this.container
                .selectAll<SVGSymbolElement, ILinkControl>("g.kinkpoint")
                .data(this.links);

            const kinkEnter = kinks
                .enter()
                .append("g")
                .attr("class", "kinkpoint")
                .attr("transform", d => this.getKinkTransform(d))
                .on("mouseover", link => this.linkMouseOver(link))
                .on("mouseout", link => this.linkMouseOut(link))
                .call(this.kinkDrag);

            newKinks =
                kinkEnter
                    .append("path")
                    .attr("d", d3.symbol().size(30).type(d3.symbolCircle))
                    .attr("id", (d: ILinkControl) => `kink_${d.id}`)
                    .attr("class", "kinkpoint-icon")
                    .style("opacity", elementOpacity) // Will be replaced by appear transition below
                    .classed("hidden", !this.buildConfig.alwaysShowKinkPoints); // Initially hidden, shown on mouse over
        }
        else {
            // Empty selections
            newKinks = d3.selectAll();
            kinks = d3.selectAll();
        }

        // Get nodes from existing
        // Note: selector is the node name, which must be unique, not the id
        const node = this.container
            .selectAll<SVGSymbolElement, INodeIcon>("g.node")
            .data(this.nodes, d => d.name);

        const nodeEnter =
            node
                .enter()
                .append("g")
                .attr("class", "node")
                .call(this.drag);

        // Highlight existing nodes if one is selected
        node.selectAll("path")

            //.style("fill",
            //    (d: INodeIcon) => (d === this.selectedNode)
            //    ? d3.rgb(this.nodeDefs.nodeColors[d.nodeType]).brighter().toString()
            //    : this.nodeDefs.nodeColors[d.nodeType])
            .attr("id", (d: INodeIcon) => this.getIdFromNodePathAndName(d.pathAndName));

        const newNodes =
            nodeEnter
                .append("path")
                .attr("d",
                    d3.symbol()
                        .size(200)
                        .type((d: INodeIcon) => this.getD3SymbolFromString(this.nodeDefs.nodeShapes[d.nodeType])))
                .attr("transform", this.nodeTransform)
                .style("fill", (d: INodeIcon) => `url(#${this.targetDivPrefix}_${d.nodeType}_gradient)`) //  "url(#myEpochGradient)")
                .style("opacity", elementOpacity) // Will be changed under appear transition below
                //    .attr("transform", this.transform)

                //    this.link.attr("d", this.pathTransform);
                //    //dragLine.attr("d", dragLineTransform);

                //    if (this.showPalette) {
                //        this.palette.attr("transform", this.transform);
                //        this.paletteText.attr("transform", this.transform);
                //    }

                //                .style("fill", d => this.nodeDefs.nodeColors[d.nodeType])

                // Drop shadow: 
                //.style("filter", "url(#drop-shadow)")

                .attr("class", d => `node-icon ${d.nodeType.toLowerCase()}`)
                .on("mouseover", icon => this.nodeMouseOver(icon, this.context))
                .on("mouseout", icon => this.nodeMouseOut(icon, this.context))
                .on("mouseup", icon => this.nodeMouseUp(icon, this.context))
                .on("click", icon => this.nodeMouseDown(icon))
                .on("dblclick", icon => this.nodeDoubleClick(icon));


        const text = this.container
            .selectAll<SVGTextElement, INodeIcon>("g.node-caption")
            .data(this.nodes, d => d.name);

        const textEnter = text
            .enter()
            .append("g")
            .attr("class", "node-caption noselect")
            .attr("transform", this.nodeTransform)
            .append("text")
            .attr("class", "node-caption-text")
            .attr("x", 22)
            .attr("y", ".31em")
            .text(d => d.splitName[0])
            .classed("node-caption-hyperlink", d => d.isShadowNode)
            .on("click", ico => {
                if (ico.isShadowNode)
                    this.nodeCaptionClick(ico);
                else
                    this.showInlineNodeNameEditor(ico);
            })

            .style("pointer-events", d => { if (d.isShadowNode) return "auto"; else return "none"; })
            .style("opacity", elementOpacity); // Will be changed by transition below

        textEnter.each(function (icon) {
            for (var i = 1; i < icon.splitName.length; i++) {
                d3.select(this)
                    .append("tspan")
                    .attr("x", 22)
                    .attr("dy", "1.2em")
                    .text(icon.splitName[i])
                    .attr("class", "node-caption noselect node-caption-tspan")
            }
        });

        // Fade in/out
        var promiseResolve;
        var promise = new Promise<any>(resolve => {
            promiseResolve = resolve;
        });

        // If there are newly created or deleted elements and this is not the initial draw, fade them in
        if (!initialRedraw) {

            // Create a list of all transitions so they are all done before tick() redraws them
            const allElements = this.container.selectAll<SVGElement, any>('*');
            const textEnterData = textEnter.data();
            const nodeEnterData = newNodes.data();
            const newLinkData = newLinks.data();
            const newKinksData = newKinks.data();

            const allNewElements = allElements.filter(d =>
                (textEnterData.indexOf(d) >= 0)
                || (nodeEnterData.indexOf(d) >= 0)
                || (newLinkData.indexOf(d) >= 0)
                || (newKinksData.indexOf(d) >= 0)
            );


            const nodeExitData = node.exit().data();
            const textEditData = text.exit().data();
            const kinkExitData = kinks.exit().data();
            const linkExitData = link.exit().data();

            const allRemovedElements = allElements.filter(d =>
                (nodeExitData.indexOf(d) >= 0)
                || (textEditData.indexOf(d) >= 0)
                || (kinkExitData.indexOf(d) >= 0)
                || (linkExitData.indexOf(d) >= 0)
            );

            await this.transitionNewElementsAsync(allNewElements)
                .then(async () =>
                    await this.transitionRemovedElementsAsync(allRemovedElements)
                        .then(() => {
                            // The transition just sets their opacity to 0, it doesn't actually remove the
                            // elements
                            allRemovedElements.remove();

                            promiseResolve();
                        }));


        }
        else
            promiseResolve();

        return promise;
    }


    showInlineNodeNameEditorAt(x, y: number, name: string,
        acceptAction: (newName: string) => void,
        cancelAction: () => void) {
        this.container
            .append("foreignObject")
            .attr("x", 22)
            .attr("y", -10)
            .attr("transform", this.nodeTransformAt(x, y))
            .attr("width", 140)
            .attr("height", 20)
            .attr("class", "node-name-editor-container")
            .html(`<input type="text" value="${name}" class="node-name-editor"/>`)
            .on("keyup", () => this.nodeEditorKeyUp(acceptAction, cancelAction));


        const editorSel = d3.select<HTMLInputElement, any>(".node-name-editor");
        editorSel.node().focus();
        editorSel.node().select();

        // Todo get rid of jquery here - d3.js thinks select() is
        //        $(editorSel.node()).select();

        //        if (removeOnBlur)
        editorSel.on("blur", () => {
            this.removeNodeNameEditor();
            cancelAction();
        });

        //    else {
        //        // This prevents other controls grabbing the focus 
        //        editorSel.on("blur", () =>
        //            setTimeout(() => editorSel.node().focus(), 20));
        //        //            editorSel.on("focusout", () => this.removeNodeNameEditor());
        //    }
    }


    private showInlineNodeNameEditor(d: INodeIcon) {

        this.showInlineNodeNameEditorAt(d.x, d.y,
            `${NodeObject.extractName(d.pathAndName)}`,
            newName => this.modelActionsHandler.renameNode(d.pathAndName, newName),
            () => { }
        );

    }

    private removeNodeNameEditor() {
        const sel = d3.select<HTMLInputElement, any>(".node-name-editor-container");
        sel.node().remove();

    }

    /**
     * Node editor keyup - handles a rename
     */
    nodeEditorKeyUp(
        acceptAction: (newName: string) => void,
        cancelAction: () => void) {

        const keyboardEvent = d3.event as KeyboardEvent; // (d3.event as KeyboardEvent);
        const target = keyboardEvent.target as HTMLInputElement;

        switch (keyboardEvent.key) {
            case "Enter":
                acceptAction(target.value);
                this.removeNodeNameEditor();
                //target.blur()
                break;

            case "Escape":
                cancelAction();
                this.removeNodeNameEditor();

                //                target.blur();
                break;

            default:
        }
    }

    async transitionNewElementsAsync(selection: d3.Selection<SVGElement, any, any, any>): Promise<any> {

        var promiseResolve;
        var promise = new Promise<any>(resolve => {
            promiseResolve = resolve;
        });

        if (selection.empty())
            promiseResolve();
        this.getFadeInTransition(selection)
            .on("end", () => promiseResolve());

        return promise;
    }


    async transitionRemovedElementsAsync(selection: d3.Selection<SVGElement, any, any, any>): Promise<any> {

        var promiseResolve;
        var promise = new Promise<any>(resolve => {
            promiseResolve = resolve;
        });

        if (selection.empty())
            promiseResolve();
        this.getDisappearTransition(selection)
            .on("end", () => promiseResolve());

        return promise;

    }


    linkMouseOut(link: ILinkControl): void {

        if (!this.buildConfig.alwaysShowKinkPoints) {
            // Hide kinkpoint on link or kink mouse over
            const kink = d3.select("#kink_" + link.id);
            kink.classed("hidden", true);
        }

    }
    linkMouseOver(link: ILinkControl): void {
        if (!this.buildConfig.alwaysShowKinkPoints) {
            // Show kinkpoint on link or kink mouse over
            const kink = d3.select("#kink_" + link.id);
            kink.classed("hidden", false);
        }
    }


    // Selects a link
    selectLink = (theLink: ILinkControl) => {
        if (theLink) {
            this.showLinkPopup(theLink);
        } else if (!this.selectedNode)
            this.hidePopupMenu();

        this.selectedLink = theLink;
    }

    // Selects a node
    selectNode(theNode: INodeIcon) {
        if (theNode) {
            this.showPopup(theNode);
            //var selCtrl = d3.select(getIdFromNodePathAndName(theNode.id));
            //selCtrl.classed("sel", true);
        } else if (!this.selectedLink)
            this.hidePopupMenu();

        this.selectedNode = theNode;

        this.nodeSelectedHandler(theNode);
        //            this.selectionChangedHandler([theNode]);

    }

    // Shows context menu for a link
    showLinkPopup = (theLink: ILinkControl) => {
        this.hidePopupMenu();

        var popup = d3.select(this.popupContainer.get(0))
            .append("div")
            .attr("class", "popup")
            //.style("left", "32px")
            //.style("top", "106px")
            .attr("id", "nodePopup");

        popup.append("strong")
            .text((theLink.source as INodeIcon).name + " to " + (theLink.target as INodeIcon).name);
        popup.append("p")
            .text(theLink.commodity + "/" + theLink.units);
        if (theLink.properties)
            popup.append("p")
                .text(`Properties: ${theLink.properties}`);

        // Create div for node buttons
        var para = popup.append("div");
        para.attr("class", "btn-toolbar");

        // Create delete button
        para.append("button")
            .attr("class", "btn btn-danger delLinkButton")
            .text("Delete (Del)");

        // Edit commodity button
        para.append("button")
            .attr("class", "btn btn-default editLinkButton")
            .text("Edit (F2)");

        // Edit commodity button
        para.append("button")
            .attr("class", "btn btn-default resetKinkPointButton")
            .text("Reset Kink Point");

        // Assign click handler to delete button
        $(".delLinkButton").on("click", () => { this.deleteLink(this.selectedLink); });

        // Assign click handler to delete button
        $(".editLinkButton").on("click", () => { this.editLink(this.selectedLink); });

        // Assign click handler to delete button
        $(".resetKinkPointButton").on("click", () => { this.resetKinkPoints([this.selectedLink]) });

    }

    // Shows context menu for a node
    showPopup = (theNode: INodeIcon) => {

        if (this.showPaletteNodeCaptions) {

            this.hidePopupMenu();

            var popup = d3.select(this.popupContainer.get(0))
                .append("div")
                .attr("class", "popup");

            popup.append("strong")
                .text(theNode.name);

            popup.append("br");

            popup.append("em")
                .text(theNode.nodeType);

            // Create div for node buttons
            var para = popup.append("div")
                .attr("class", "btn-toolbar");

            if (!theNode.isShadowNode) {

                // Create rename button
                para.append("button")
                    .attr("class", "btn btn-default renameNodeButton")
                    .text("Rename (F2)");

                if (this.linkingNode.pathAndName) {
                    popup.append("p")
                        .text("Click target node to complete link");

                    // Cancel link button
                    para.append("button")
                        .attr("class", "btn btn-default cancelLinkButton")
                        .text("Cancel Link");

                    // Assign click handler to cancel link button
                    $(".cancelLinkButton").on("click",
                        () => {
                            this.linkingNode.pathAndName = "";
                            this.showPopup(theNode);
                        });

                } else {
                    // Create link button
                    para.append("button")
                        .attr("class", "btn btn-default linkNodeButton")
                        .text("Link...");
                }

                //                popup.append("br");

                // Create another div for more buttons
                const para2 = popup.append("div")
                    .attr("class", "btn-toolbar");

                // Create delete button
                para2.append("button")
                    .attr("class", "btn btn-warning delNodeButton")
                    .text("Delete (Del)");
                // Create show node data button
                para2.append("button")
                    .attr("class", "btn btn-default editNodeDataButton")
                    .text("Edit Data");

                // Assign click handler to delete button
                $(".delNodeButton").on("click", () => { this.deleteSelection(); });

                // Assign click handler to delete button
                $(".renameNodeButton").on("click", () => { this.renameNode(this.selectedNode); });

                // Assign click handler to link button
                $(".linkNodeButton").on("click", () => { this.linkNode(this.selectedNode); });

                // Assign click handler to show node data button
                $(".editNodeDataButton").on("click", () => { this.editNodeData(this.selectedNode); });
            }
        }
    }

    // Hides the popup menu
    hidePopupMenu = () => {
        var thePopup = d3.select(".popup");
        if (thePopup)
            thePopup.remove();
    }


    //// Transform function for links
    //pathTransform(d: ILinkControl): string {
    //    var dr = 0;
    //    return `M${(d.source as INodeIcon).x},${(d.source as INodeIcon).y}A${dr},${dr} 0 0,1 ${(d.target as INodeIcon).x},${(d.target as INodeIcon).y}`;
    //}

    //// Transform function for nodes
    //transform(d: INodeIcon | INodeDef): string {
    //    return `translate(${d.x},${d.y})`;
    //}

    private doCopyOrCut = (isCut: boolean): boolean => {
        // Get selected nodes
        var selectedNodes: d3.Selection<SVGSymbolElement, INodeIcon, any, any> = d3.selectAll(".node-icon.selected");

        if (selectedNodes.size() > 0) {
            // Filter out shadows
            selectedNodes =
                selectedNodes.filter((curNode: INodeIcon) => !curNode.isShadowNode);

            // Extract node names

            var nodeFullPaths = new Array<string>();
            selectedNodes.each(curNode => nodeFullPaths.push(curNode.pathAndName));

            // Unhighlight all nodes as being copied
            d3.selectAll(".node-icon").classed("cutting", false);

            // If its a cut provide visual indication of nodes being cut
            if (isCut)
                selectedNodes.classed("cutting", true);

            this.modelActionsHandler.copyNodesToClipboard(isCut, nodeFullPaths);
            return true;
        } else
            return false;

    }

    /**
     * Copies nodes to the clipboard
     */
    copyDiagramSelection = (): boolean => {
        return this.doCopyOrCut(false);

    }


    // Copies selected nodes
    cutDiagramSelection = () => {
        this.doCopyOrCut(true);
    }

    getSelectedNodes(): INodeIcon[] {
        var selected: d3.Selection<SVGSymbolElement, INodeIcon, any, any> = d3.selectAll(".node-icon.selected");

        var result = [];
        selected.each(n => {
            if (!n.isShadowNode)
                result.push(n);
        });
        return result;
    }

    // Deletes user selection
    deleteSelection() {

        // Multi-select delete?
        var selected: d3.Selection<SVGSymbolElement, INodeIcon, any, any> = d3.selectAll(`${this.getTargetDivId()} .node-icon.selected`);
        if (selected.size() > 0) {
            var toDelete = [];

            selected.each(n => {
                if (!n.isShadowNode)
                    toDelete.push(n);
            });

            // New way - deletion is done in this handler,
            // then we later handle deletions in IModelObserver.nodesDeleted
            this.modelActionsHandler.deleteNodes(toDelete);

        } else if (this.selectedLink)
            this.deleteLink(this.selectedLink);

    }

    selectAllNodes() {
        d3.selectAll(".node-icon").classed("selected", true);
    }

    deselectAllNodes() {
        d3.selectAll(".node-icon").classed("selected", false);
    }


    /**
     * Fixes node positions so force doesn't affect them again
     */
    fixNodePositions() {

        var newPositions = new Array<INodeDiagramPosition>();

        this.nodes.forEach((n: INodeIcon) => {

            // We also have to tell the server that the node position has been set
            // So it doesn't move about when we revisit this sector
            if (!this.isNodeFixed(n)) {

                this.fixNode(n);

                newPositions.push({
                    nodePathAndName: this.getDiagramNodePathAndName(n), // Note: have to retain shadow name
                    xPos: n.x,
                    yPos: n.y
                });

            }

        });

        // Send the reposition commands
        if (newPositions.length > 0)
            this.modelActionsHandler.setNodePositions(newPositions);

    }

    /**
     * Hightlights nodes (used to show pasted nodes)
     */
    highlightNodes(nodePathAndNames: string[]): void {

        // Highlight node
        var icons: d3.Selection<SVGSymbolElement, INodeIcon, any, any> = d3.selectAll(".node-icon");

        icons
            .classed("selected",
                (curIcon: INodeIcon) => {

                    let found = false;
                    nodePathAndNames.some((curNode: string) => {
                        if (curNode === curIcon.pathAndName) {
                            found = true;
                            return true;
                        } else
                            return false;
                    });

                    return found;
                });

        this.reDraw();
        this.tick();
    }

    /**
     * Focuses on a node.  Called when a node is created by us, or
     * a node is selected in the model tree.
     */
    showNode(selectedVal: string) {
        //find the node
        var node = this.svg.selectAll(".node");
        if (selectedVal === "none") {
            node.style("stroke", "white").style("stroke-width", "1");
        }

        //// Fade other nodes
        //selected.style("opacity", "0.15");
        //d3.selectAll(".node")
        //    .transition()
        //    .delay(5000)
        //    .duration(1000)
        //    .style("opacity", 1);
        // Scroll to the node
        var selNodes = this.nodes.filter(d => d.name === selectedVal);
        if (selNodes.length > 0) {

            const nodeToFocus = selNodes[0];

            // Following is not yet implemented because selectNode is not a class level
            // function
            // selectNode(nodeToFocus);

            // Do not scroll to it if we created it on the palette
            if (this.lastCreatedNodeViaPalette !== selectedVal) {

                // Highlight node(s)
                d3.selectAll<SVGSymbolElement, INodeIcon>(".node-icon")
                    .classed("selected", d => d.name === selectedVal);

                this.lastCreatedNodeViaPalette = "";
                // Todo fix
                const theScale = this.diagramState.scale; //  /*this.zoom.scale(); */100;

                const thePos: [number, number] = [
                    this.width / 2 - theScale * nodeToFocus.x, this.height / 2 - theScale * nodeToFocus.y
                ];

                this.svg.transition()
                    .duration(750)
                    .call(this.zoom.transform,
                        d3.zoomIdentity.translate(
                            thePos[0], thePos[1])
                            .scale(theScale));

                this.diagramState.xPos = thePos[0];
                this.diagramState.yPos = thePos[1];


                // this.container.attr("transform", `translate(${thePos})scale(${theScale})`);
            }
        }
    }

    // Centers the diagram in case the user gets lost
    centerDiagram(transition: boolean): Promise<void> {

        if (this.nodes.length > 0) {

            let minX = 0,
                maxX = 0,
                minY = 0,
                maxY = 0;

            this.nodes.forEach(node => {

                if (minX === 0 || node.x < minX)
                    minX = node.x;

                if (maxX === 0 || node.x > maxX)
                    maxX = node.x;

                if (minY === 0 || node.y < minY)
                    minY = node.y;

                if (maxY === 0 || node.y > maxY)
                    maxY = node.y;

            });


            const theScale = this.diagramState.scale; // zoom.scale();

            const targetX = minX + ((maxX - minX) / 2);
            const targetY = minY + ((maxY - minY) / 2);

            const thePos: [number, number] = [
                this.width / 2 - theScale * targetX,
                this.height / 2 - theScale * targetY
            ];


            // Hackery
            var promiseResolve;
            var promise = new Promise<any>(resolve => {
                promiseResolve = resolve;
            });


            this.svg
                .transition()
                .duration(transition ? 750 : 0)
                .call(this.zoom.transform,
                    d3.zoomIdentity.translate(
                        //  275 + (this.width/ 2), -227.5,
                        //targetX, targetY
                        thePos[0], thePos[1]
                    )
                        .scale(theScale))
                .on("end", () => {
                    this.diagramState.setState(thePos[0], thePos[1], theScale);
                    promiseResolve();
                });
        }

        return promise;
    }

    /**
     * Key down of body
     *
     */
    bodyKeydown() {
        // Check for pasting somewhere else - don't want to capture it


        const keyboardEvent = d3.event as KeyboardEvent; // (d3.event as KeyboardEvent);
        const eventSourceName: string = (keyboardEvent.srcElement as Element).localName || "";
        // Todo: fix this 
        if (eventSourceName !== "body" // Chrome
            &&
            eventSourceName !== "svg") // IE
            return;

        //if (keyboardEvent.key === "Control")
        //  return;

        switch (keyboardEvent.key) {
            case "F2":
                {
                    const selectedNodes = d3.selectAll<SVGSymbolElement, INodeIcon>(".node-icon.selected");
                    if (selectedNodes.size() === 1) {
                        this.showInlineNodeNameEditor(selectedNodes.data()[0]);
                    }
                    else if (this.selectedLink)
                        this.editLink(this.selectedLink);

                    break;
                }
            case "Delete": // Delete

                this.deleteSelection();

                break;

            case "c": // C (for Ctrl-C copy)
                {
                    if (keyboardEvent.ctrlKey) {
                        //this.clipBrd.setIsCutting(false);
                        this.copyDiagramSelection();
                        keyboardEvent.stopPropagation();
                    }
                    break;
                }

            // Not needed: captured by parent window
            //case 86: // V (for Ctrl-V paste)
            //{
            //    if (keyboardEvent.ctrlKey) {
            //        this.pasteToDiagram();
            //        keyboardEvent.stopPropagation();
            //    }
            //    break;
            //}

            case "a": // A (for Ctrl-A copy)
                {
                    if (keyboardEvent.ctrlKey) {
                        this.selectAllNodes();
                        keyboardEvent.stopPropagation();
                        keyboardEvent.preventDefault();
                    }
                    break;
                }


            case "x": // X (for Ctrl-X cut)
                {
                    if (keyboardEvent.ctrlKey) {
                        this.cutDiagramSelection();
                        keyboardEvent.stopPropagation();
                    }
                    break;
                }

            case "d": // Debug mode
                {
                    if (keyboardEvent.ctrlKey && keyboardEvent.altKey)
                        this.buildConfig.forceSimulationConfig.renderFalseNodes = !this.buildConfig.forceSimulationConfig.renderFalseNodes;

                    if (this.buildConfig.forceSimulationConfig.createFalseNodesForSimulation)
                        this.redrawFalseNodes();

                    if (this.buildConfig.forceSimulationConfig.renderFalseNodes)
                        this.tickFalseElements();
                    else
                        this.clearDebugElements();


                }

            case "Escape": // Escape to cancel copy/cut
                {
                    d3.select(".node-icon").classed("cutting", false);
                    break;
                }
        }
    }

    clearDebugElements() {
        this.svg.selectAll("g.caption-node").remove()
        this.svg.selectAll("g.caption-link").remove()
        this.svg.selectAll("g.kink-node").remove()
    }


    /**
     * Ctor
     * @param width
     * @param height
     * @param modelValidation
     * @param editNodeDataHandler
     * @param nodeLinkClickHandler
     * @param clipBrd
     * @param busyIndicatorFunc
     * @param showLinkDataHandler
     * @param nodeDefs
     * @param commoditySelector
     */
    constructor(
        getDimensionsFunc: (selectorStr) => { width: number, height: number },
        // paletteHeight: number,
        //paletteNodes: Array<INodeIcon>,
        modelValidation: ModelUtils,
        editNodeDataHandler: (nodeId: string) => void,
        nodeLinkClickHandler: (nodeId: string, boolean) => void,
        showLinkDataHandler,
        nodeDefs: NodeDefs,
        commoditySelector: ICommoditySelector,
        popupContainer: JQuery,
        modelActionsHandler: IModelActionsHandler,
        nodeSelectedHandler: (node: INodeObject | INodeObject[]) => void,
            /*selectionChangedHandler: (nodes: INodeObject[]) => void*/) {

        this.getDimensionsFunc = getDimensionsFunc;
        this.resize(this.svg);
        //const dims = getDimensionsFunc();

        //this.width = dims.width,
        //this.height = dims.height;

        this.commoditySelector = commoditySelector;
        this.modelValidation = modelValidation;
        this.editNodeDataHandler = editNodeDataHandler;
        this.nodeLinkClickHandler = nodeLinkClickHandler;
        //this.clipBrd = clipBrd;
        this.showLinkDataHandler = showLinkDataHandler;
        this.nodeDefs = nodeDefs;
        this.commoditySelector = commoditySelector;
        this.popupContainer = popupContainer;

        this.modelActionsHandler = modelActionsHandler;
        this.nodeSelectedHandler = nodeSelectedHandler;
        //            this.selectionChangedHandler = selectionChangedHandler;

    }

    /**
     * Builds the diagram
     */
    async buildDiagram(origLinks: Array<ILinkControl>,
        origNodes: Array<IOldNodeIcon>,
        sectorPathAndName: string,
        initialNodeName: string,
        linkingNode: ILinkingNode, // mb.common.NodeRecord,
        diagramState: NetworkDiagramState,
        showPaletteNodeCaptions: boolean,
        handleKeyboardEvents: boolean,
        targetDivName: string,
        targetDivPrefix,
        buildConfig: NetworkDiagramConfig
    ): Promise<NetworkDiagramBuilder> {

        this.targetDivName = targetDivName;
        this.targetDivPrefix = targetDivPrefix;

        this.initialNodeName = initialNodeName;
        this.linkingNode = linkingNode;
        this.diagramState = diagramState;
        this.showPaletteNodeCaptions = showPaletteNodeCaptions;
        this.buildConfig = buildConfig;
        this.handleKeyboardEvents = handleKeyboardEvents;

        this.context =
        {
            hasDragged: false,
            isDragging: false,
            nodeUnderMouse: null,
            sectorPathAndName: sectorPathAndName,
            doingPaletteDrag: false
        };

        // Initialize this.zoom
        this.zoom = d3Zoom.zoom<SVGSVGElement, any>()
            .scaleExtent([0.25, 10])
            .on("zoom", () => this.zoomed())
            .on("start", () => this.zoomStart())
            .on("end", () => this.zoomEnd());


        this.drag =
            //            nodeDrag;
            d3Drag.drag<SVGCircleElement, INodeIcon>()
                .subject((theNode: INodeIcon) => { return { x: theNode.x, y: theNode.y } })
                //            .origin((theNode: INodeIcon) => {
                //              return { x: theNode.x, y: theNode.y }
                //        })
                .on("start", (icon) => this.dragstarted(icon, this.context))
                .on("drag", (icon) => this.dragged(icon, this.context))
                .on("end", (icon) => this.dragended(icon, this.context, this));

        var paletteDrag: d3.DragBehavior<SVGElement, INodeDef, any> = d3Drag.drag<SVGCircleElement, INodeDef, any>()
            .subject((theNode: INodeDef) => { return { x: theNode.x, y: theNode.y } })
            .on("start", d => this.paletteDragstarted(d, this))
            .on("drag", d => this.paletteDragged(d, this.context, this))
            .on("end", nodeDef => this.paletteDragended(nodeDef, this.context));

        // Kink drag definition
        this.kinkDrag =
            d3Drag.drag<SVGSymbolElement, ILinkControl, ILinkControl>()
                .subject(theNode => theNode)
                .on("start", (kink, event) => this.kinkDragStarted(kink))
                .on("drag", (kink, event) => this.kinkDragged(kink))
                .on("end", (kink, event) => this.kinkDragEnded(kink));


        // Nodes passed in have fixed property instead of fx an fy as in d3.js > v3
        this.nodes = origNodes.map(old => {
            const isShadowNode = NodeObject.isShadowNodeName(old.name);
            return {
                //color: old.color,
                x: old.x,
                y: old.y,
                //fixed: old.fixed,
                fx: old.fixed ? old.x : null,
                fy: old.fixed ? old.y : null,
                name: old.name,
                pathAndName: old.pathAndName,
                isFolder: old.isFolder,
                nodeType: old.nodeType,
                //shape: old.shape,
                path: old.path,
                fqmId: old.fqmId,
                splitName: this.getSplitNodeName(old.name, isShadowNode),
                isShadowNode: isShadowNode,
                selectionTime: 0

            };
        });

        // this.or force.nodes();
        //this.links = this. force.links();


        // Link source.target fixups so we know its the node, not an index, for easier casting later
        origLinks.forEach(link => {

            if (typeof (link.source) === "number")
                link.source = this.nodes[link.source];

            if (typeof (link.target) === "number")
                link.target = this.nodes[link.target];

            link.id = NetworkDiagramBuilder.getRandomElementId(); // Random id
        });

        this.links = origLinks;


        //var labelLayout = d3.layout.force(nodes)
        //    .force("charge", d3.forceManyBody().strength(-50))
        //    .force("link", d3.forceLink(label.links).distance(0).strength(2));

        // Define 'div' for link tooltips
        // Doesn't seem to work well anymore: disabled
        //this.linkHintDiv = d3.select(this.popupContainer.get(0)) //diagrand  d3.select("body")
        //    .append("div") // declare the tooltip div 
        //    .attr("class", "tooltip") // apply the 'tooltip' class
        //    .style("opacity", 0);

        this.svg = d3
            .select<SVGSVGElement, undefined>(targetDivName)
            .datum<SVGDatum>({ width: this.width, height: this.height })
            //.append("svg")
            //.attr("width", width)
            //.attr("height", height + paletteHeight)
            .attr("shape-rendering", "geometricPrecision")
            //.on('mousemove', mouseMove) What is this for?
            .call(this.zoom)
            .on("dblclick.zoom", null);

        // Calculate initial size
        this.resize(this.svg);

        this.container = this.svg.append("g")
            .attr("transform",
                `translate(${0},${0})`);

        /* Drop shadow stuff */

        // filters go in defs element
        var defs = this.svg.append("defs");

        // create filter with id #drop-shadow
        // height=130% so that the shadow is not clipped
        var filter = defs.append("filter")
            .attr("id", "drop-shadow")
            .attr("height", "150%")
            .attr("width", "150%");

        // SourceAlpha refers to opacity of graphic that this filter will be applied to
        // convolve that with a Gaussian with standard deviation 3 and store result
        // in blur
        filter.append("feGaussianBlur")
            .attr("in", "SourceGraphic")
            .attr("stdDeviation", 2)
            .attr("result", "blur");

        // overlay original SourceGraphic over translated blurred opacity by using
        // feMerge filter. Order of specifying inputs is important!
        var feMerge = filter.append("feMerge");

        feMerge.append("feMergeNode")
            .attr("in", "offsetBlur")
        feMerge.append("feMergeNode")
            .attr("in", "SourceGraphic");

        /* End drop shadow stuff */

        //Gradient definitions

        this.nodeDefs.paletteNodes(true).forEach(d => {
            var gradient = defs
                .append("svg:defs")
                .append("svg:linearGradient")
                .attr("id", `${this.targetDivPrefix}_${d.name}_gradient`)
                .attr("gradientUnits", "objectBoundingBox")
                .attr("x1", "0%").attr("y1", "0%")
                .attr("x2", "100%").attr("y2", "0%");

            gradient.append("svg:stop")
                .attr("offset", "0%")
                .attr("stop-opacity", 1)
                .attr("stop-color", d3.rgb(d.color).brighter().toString());// (d: INodeDef) => d.color);

            gradient.append("svg:stop")
                .attr("offset", "100%")
                .attr("stop-opacity", 1)
                .attr("stop-color", d3.rgb(d.color).darker().toString()); //  "blue"); // (d: INodeDef) => d.color + 80);
        });

        d3.select(window)
            .on("resize", () => this.resize(this.svg));

        // Create keydown handler, for deleting and renaming nodes

        if (this.handleKeyboardEvents)
            d3.select("body")
                .on("keydown", () => this.bodyKeydown());

        // Arrowheads for connected nodes
        this.svg.append("svg:defs")
            .selectAll("marker")
            .data(["end"])
            .enter()
            .append("svg:marker")
            .attr("id", `${this.targetDivPrefix}_end`)
            .attr("viewBox", "0 -5 10 10")
            .attr("refX", 16)
            .attr("refY", -0)
            .attr("markerWidth", 5)
            .attr("markerHeight", 5)
            .attr("orient", "auto")
            .append("svg:path")
            .attr("d", "M0,-5L10,0L0,5");

        // Arrowheads for drag nodes
        this.svg.append("svg:defs")
            .selectAll("marker")
            .data(["dragend"])
            .enter()
            .append("svg:marker")
            .attr("id", String)
            .attr("viewBox", "0 -5 10 10")
            .attr("refX", 10)
            .attr("refY", -0)
            .attr("markerWidth", 5)
            .attr("markerHeight", 5)
            .attr("orient", "auto")
            .append("svg:path")
            .attr("d", "M0,-5L10,0L0,5");

        // line displayed when dragging new nodes
        // Static because it never changes
        this.dragLine = this.container
            .append("g")
            .append("line")
            .attr("class", "line dragline hidden")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", 0)
            .attr("marker-end", "url(#dragend)");


        // Create curve for link lines
        this.curve = d3.line<ICoordinate>()
            .x(d => d.x)
            .y(d => d.y)
            .curve(d3.curveNatural);


        // Setup mouse handlers for selection rect
        //        this.svg
        //          .on("mousedown", () => this.svgMouseDown())
        //            .on("mousemove", () => this.svgMouseMove())
        //            .on("mouseup", () => this.svgMouseUp())
        //            .on("mouseout", () => this.svgMouseOut());

        // Entry point
        this.reDraw(true);

        // Unless the user is trying to link a node, we want to hide the popup menu
        if (!this.linkingNode.pathAndName)
            this.hidePopupMenu();

        var paletteContainer = this.svg.append("g");

        /*
        Not working yet
        var palettePanel = d3.select(targetDivName)
            .append("div")
            .attr("class", "palettePanel");
            //.style("top", paletteHeight + "px");*/

        var nodesForPalette = this.nodeDefs.paletteNodes(false);

        if (this.buildConfig.showPalette) {

            const palette = paletteContainer
                .append("g")
                .selectAll<SVGSymbolElement, INodeDef>(".paletteNode")
                .data(nodesForPalette);

            palette
                .enter()
                .append("path")
                //                .attr("class", "paletteNode")
                .attr("id", (d: INodeDef) => this.getPaletteElementIdFromName(d.name))
                .attr("d",
                    d3.symbol()
                        .size(200)
                        .type((d: INodeDef) => this.getD3SymbolFromString(d.shape))
                )
                .attr("r", 40)
                .attr("x", 200)
                .attr("y", 500)
                .style("fill", d => `url(#${this.targetDivPrefix}_${d.name}_gradient)`) //  "url(#myEpochGradient)")
                //.attr("transform", d => `translate(${d.x},${d.y})`)


                //.style("fill", (d: INodeDef) => d.color)

                //.attr("color", (d: INodeDef) => d.color)

                // Drop shadow: 
                //.style("filter", "url(#drop-shadow)")

                // Gradient:
                //.style("fill","url(#myEpochGradient)")

                .attr("class", "palette-icon")
                .call(paletteDrag)
                // Palette hints
                .on("mouseover",
                    () => {
                        //const target = (d3.event as MouseEvent).currentTarget;

                        //if (!this.showPaletteNodeCaptions && !d3.select(target).classed("dragging")) {
                        //    // Show link tooltip
                        //    this.linkHintDiv.transition()
                        //        .delay(500)
                        //        .duration(500)
                        //        .style("opacity", 0);
                        //    this.linkHintDiv.transition()
                        //        .delay(500)
                        //        .duration(200)
                        //        .style("opacity", .9);

                        //    const event = d3.event as BaseJQueryEventObject;

                        //    this.linkHintDiv.html(d.name)
                        //        .style("left", /*(event.pageX)*/ + "px")
                        //        .style("top", /*(event.pageY*/ "-28px");
                        //}
                    })
                .on("mouseout",
                    () => {
                        //const event = d3.event as d3.BaseEvent;

                        //if (!this.showPaletteNodeCaptions &&
                        //    !d3.select(event.sourceEvent.currentTarget).classed("dragging")) {
                        //    // Hide the link tooltip
                        //    this.linkHintDiv
                        //        .transition()
                        //        .delay(100)
                        //        .duration(600)
                        //        .style("opacity", 0)
                        //        .style("pointer-events", "none");
                        //}
                    });


            //palette.append("circle").attr("r", 5);

            const paletteText = paletteContainer
                .append("g")
                .selectAll<SVGTextElement, INodeDef>("text");

            paletteText
                .data(nodesForPalette)
                .enter().append("text")
                .attr("x", 12)
                .attr("y", ".31em")
                .attr("class", "paletteText noselect")
                .text(d => this.showPaletteNodeCaptions ? d.name : "")
                .attr("transform", d => `translate(${d.x},${d.y})`);

            paletteContainer
                //                    .enter()
                .append("line")
                .attr("class", "palette-border")
                .attr("x1", 0)
                .attr("y1", 50)
                .attr("x2", this.width)
                .attr("y2", 50);
            //    .style("stroke", "lightgreen")
            //              .style("stroke-width", 10)


            this.force = d3Force
                .forceSimulation()
                //                .force("charge", d3Force.forceManyBody().strength(-1000))
                .force("x", d3.forceX())
                .force("y", d3.forceY())
                // push nodes apart to space them out
                .force("charge", d3.forceManyBody().strength(-225))
                // add some collision detection so they don't overlap
                .force("collide", d3.forceCollide().radius((d) => getNodeForceRadius(d)))
                // and draw them around the centre of the space
                //.force("center", d3.forceCenter(this.width, this.height))


                //                .force("collide", d3.forceCollide(50))
                .on("tick", () => this.tick())
                .stop();


            // Auto-layout unfixed nodes
            // E.g. when a model is loaded without node positions for any reason
            this.startSimulation(this.nodes.filter(node => !this.isNodeFixed(node)), false, this.buildConfig.autoCreateKinkPoints);

            // After this force, fix all nodes so they don't move about any more
            this.fixNodePositions();
        }


        // Set initial scale
        var theScale = Math.max(this.diagramState.scale, 0.25);
        theScale = Math.min(theScale, 10);
        if (isNaN(theScale))
            theScale = 1;

        // If an initial focus node was specified, target that
        let thePos = { x: 1, y: 1 };

        if (this.initialNodeName) {
            var initialNode = this.findNode(this.initialNodeName);
            if (initialNode) {
                thePos = {
                    x: this.width / 2 - theScale * initialNode.x,
                    y: this.height / 2 - theScale * initialNode.y
                };

                // Highlight node
                d3.selectAll<SVGSymbolElement, INodeIcon>(`${this.getTargetDivId} .node-icon`)
                    //                    .filter(d =>  d.id === initialNode.pathAndName)
                    .filter(d => d.pathAndName === initialNode.pathAndName)
                    .classed("selected", true);

                // Show node editor
                this.selectNode(initialNode);

                this.diagramState.xPos = thePos.x;
                this.diagramState.yPos = thePos.y;

            }
        } else

            // Otherwise focus on saved position state
            if (this.diagramState.isValid())
                thePos = { x: this.diagramState.xPos, y: this.diagramState.yPos };

        this.svg
            //            .transition()
            //           .duration(750)
            .call(this.zoom.transform,
                d3.zoomIdentity.translate(
                    thePos.x, thePos.y)
                    .scale(theScale));

        //this.svg.transition()
        //    .duration(750)
        //    .call(this.zoom.translate(thePos).scale(theScale).event);


        //this.container.attr("transform", `translate(${thePos})scale(${theScale})`);

        this.tick();

        if (buildConfig.centerDiagram)
            await this.centerDiagram(false)

        return this;
    }

    kinkDragStarted(link: ILinkControl): void {
        var dragTarget = d3.select("#kink_" + link.id);
        dragTarget.classed("selected", true);
    }

    static isValidCoordinate(kink: ICoordinate) {
        return kink != null && (kink.x !== 0 || kink.y !== 0);
    }

    kinkDragged(kink: ILinkControl): void {

        let dragEvent = (d3.event as d3Drag.D3DragEvent<SVGSymbolElement, ILinkControl, ILinkControl | d3Drag.SubjectPosition>); // d3Drag.D3DragEvent);

        if (dragEvent.dx !== 0 || dragEvent.dy !== 0) {

            // If its the first drag, it will be an uninitialized kink point
            if (!NetworkDiagramBuilder.isValidCoordinate(kink.kinkPoint))
                kink.kinkPoint = this.getDefaultKinkPointPos(kink.source, kink.target);

            kink.kinkPoint.x += dragEvent.dx;
            kink.kinkPoint.y += dragEvent.dy;

            this.tick();
        }
    }

    kinkDragEnded(link: ILinkControl): void {
        var dragTarget = d3.select("#kink_" + link.id);
        dragTarget.classed("selected", false);
        this.saveNodePositions([], [link]);
    }

    normalizeKinkPoint(src: ICoordinate, dest: ICoordinate, kinkPoint: ICoordinate): ICoordinate {
        var tempKp = { x: 0, y: 0 };

        if (kinkPoint && (kinkPoint.x !== 0 && kinkPoint.y !== 0)) {
            tempKp.x = kinkPoint.x;
            tempKp.y = kinkPoint.y;
        }
        else
            tempKp = this.getDefaultKinkPointPos(src, dest);

        return tempKp;
    }


    getLinkPoints(src: ICoordinate, dest: ICoordinate, kinkPoint: ICoordinate): ICoordinate[] {

        if (this.buildConfig.applyKinkPointsToLinks) {
            var tempKp = this.normalizeKinkPoint(src, dest, kinkPoint);

            // Kinkpoint is stored as a point relative to src
            const absKinkPoint = {
                x: src.x + tempKp.x, y: src.y + tempKp.y
            };

            return [{ x: src.x, y: src.y }, absKinkPoint, { x: dest.x, y: dest.y }];
        }
        else
            return [{ x: src.x, y: src.y }, { x: dest.x, y: dest.y }];


    }

    getDefaultKinkPointPos(src: ICoordinate, dest: ICoordinate): ICoordinate {
        const midPoint = {
            x: (dest.x + src.x) / 2, y: (dest.y + src.y) / 2
        };

        // Return it as a coordinate relative to the source node
        return { x: midPoint.x - src.x, y: midPoint.y - src.y };
    }

    getTargetDivId() {
        return `${this.targetDivName}`;
    }

    zoomStart() {

        let sourceEvent = (d3.event as d3.BaseEvent);
        let event = sourceEvent.sourceEvent as MouseEvent;

        if (event) {
            //if (!isDragging) 
            // Clear all selections
            if (!event.ctrlKey) {
                d3.selectAll(`${this.targetDivName} .node-icon`).classed("selected", false);
                d3.selectAll(`${this.targetDivName} .link`).classed("sel", false);
                this.selectNode(null);
                this.selectLink(null);
            }
            if (event.shiftKey) {

                // Have to take into account the pan when seeing if nodes are in selection

                // Todo fix 
                //this.curScale = this.diagramState.scale; // this.zoom.scale();
                // Todo fix 
                //this.curScroll = [this.diagramState.xPos, this.diagramState.yPos];  // this.zoom.translate();

                this.selecting = true;
                // Temporarily disable this.zoom
                //this.zoom.on("zoom", null);
                this.zoom.on("zoom", () => this.shiftZoomed());

                //this.svg.call(this.zoom) // Alan: may be d3.zoom
                //  .on("mousedown.zoom", null);

                // Todo fix
                //const p =  d3.pointer(event.currentTarget);
                // (d3.event as MouseEvent).currentTarget;
                // d3.mouse((d3.event as d3.BaseEvent).sourceEvent.currentTarget);
                let p = d3.mouse(this.svg.node());


                this.svg.append("rect")
                    .attr(//x: 6,
                        //ry: 6,
                        "class", "selection")
                    .attr("x", p[0])
                    .attr("y", p[1])
                    .attr("width", 0)
                    .attr("height", 0);
            }
        }
    }

    svgMouseMove() {


        let sourceEvent = (d3.event as d3.BaseEvent);
        let ev = sourceEvent.sourceEvent as MouseEvent;
        //        let ev = (d3.event as d3Zoom.D3ZoomEvent<SVGSVGElement, any>);

        //var ev = d3.event as d3.ZoomEvent;
        //d3.mouse(this);

        if (ev.shiftKey) {
            const s = this.svg.select<SVGRectElement>("rect.selection");

            if (!s.empty()) {

                let p = d3.mouse(this.svg.node());
                //const p = ev.currentTarget; // d3.pointer(event as MouseEvent); //.currentTarget);
                //const p = [ev.x, ev.y];
                let d = {
                    x: parseInt(s.attr("x"), 10),
                    y: parseInt(s.attr("y"), 10),
                    width: parseInt(s.attr("width"), 10),
                    height: parseInt(s.attr("height"), 10)
                };
                const move = {
                    x: p[0] - d.x,
                    y: p[1] - d.y
                };
                if (move.x < 1 || (move.x * 2 < d.width)) {
                    d.x = p[0];
                    d.width -= move.x;
                } else {
                    d.width = move.x;
                }

                if (move.y < 1 || (move.y * 2 < d.height)) {
                    d.y = p[1];
                    d.height -= move.y;
                } else {
                    d.height = move.y;
                }

                if (!(isNaN(d.width) || isNaN(d.height)) && d.width > 0 && d.height > 0) {
                    //  s.attr("y", d.y);
                    //  s.attr("x", d.x);
                    s.attr("height", d.height);
                    s.attr("width", d.width);
                }

                // deselect all temporary selected state objects
                d3.selectAll(`${this.targetDivName} .node-icon`).classed("selected", false);

                // Not we have to use the old function() instead of =>
                // for this, because we need access to the element (this)
                // within the function
                var realThis = this;

                //var absPos = { x: d.x - curOffset[0], y: d.y - curOffset[1] };
                const absPos = {
                    x: (d.x - realThis.diagramState.xPos) / realThis.diagramState.scale,
                    y: (d.y - realThis.diagramState.yPos) / realThis.diagramState.scale
                };

                const radius = 0.5 / realThis.diagramState.scale; // curScale;

                var nodes = d3.selectAll<SVGSymbolElement, INodeIcon>(`${this.targetDivName} .node-icon`);
                //.filter((theNode, i) => {
                //    return !theNode.classed("selected");
                //});


                nodes.each(function (icon) {
                    //                                    var element = d3.select(n[theI]);
                    var element: any = d3.select(this); // this.ownerSVGElement; //stateData;

                    //const eventTarget = (d3.event as d3.BaseEvent).sourceEvent.currentTarget;

                    // Todo: Check this works
                    //const element = d3.select((d3.event as KeyboardEvent).target);
                    //const element = d3.select(stateData);

                    //const element = this.findNodeIcon(icon.name); // d3.select(icon.name); // element = d3.select(this);

                    let nodex = icon.x;  //parseFloat(element.attr("x"));
                    let nodey = icon.y; // parseFloat(element.attr("y"));

                    if (
                        !element.classed("selected") &&
                        // inner circle inside selection frame
                        nodex - radius >= absPos.x &&
                        nodex + radius <= absPos.x + d.width &&
                        nodey - radius >= absPos.y &&
                        nodey + radius <= absPos.y + d.height
                    ) {

                        element //.parentNode)
                            // .classed("selection", true)
                            .classed("selected", true);

                    }
                });
            }
        }
    }


    zoomEnd() {

        if (this.selecting) {


            // remove selection frame
            this.svg.selectAll("rect.selection").remove();

            // Alan what is this
            //this.zoom.call(this.zoom.transform,
            //    d3.zoomIdentity.translate(
            //        this.curScroll[0], this.curScroll[1])
            //        .scale(this.curScale));

            // Restore this.zoom function
            this.svg.call(this.zoom) // Alan: maybe this.zoom
                .on("dblclick.zoom", null);

            this.selecting = false;

            // Trigger selection change event
            var selected = d3.selectAll<SVGSymbolElement, INodeIcon>(`${this.targetDivName} .node-icon.selected`);

            var selectedObjs = [];
            selected.each(n => {
                if (!n.isShadowNode)
                    selectedObjs.push(n);
            });
            //this.selectionChangedHandler(selectedObjs);

            this.nodeSelectedHandler(selectedObjs);


            // Restore zoom behavior
            this.zoom.on("zoom", () => this.zoomed());
            //this.zoom.on("end", () => this.zoomEnd);

        }

        else {

            // Not a selection
            // Save scale for later saving to db
            //    let zoomEvent = (d3.event as d3Zoom.D3ZoomEvent<SVGSVGElement, any>);
            //    const x = zoomEvent.transform.x;
            //    const y = zoomEvent.transform.y;
            //    const scale = zoomEvent.transform.k;
            //    this.diagramState.setState(x, y, scale);

        }
    }

    svgMouseOut() {
        {
            var eventTarget = (d3.event as BaseJQueryEventObject).relatedTarget;
            //(event /*d3.event*/ as BaseJQueryEventObject).relatedTarget;

            //if (d3.event.shiftKey) 
            if (eventTarget && eventTarget.tagName === "HTML") {
                // remove selection frame
                this.svg.selectAll("rect.selection").remove();

                // remove temporary selection marker class
                d3.selectAll(`${this.targetDivName} .node-icon`).classed("selected", false);

                //if (this.curScroll) {
                //this.zoom.call(this.zoom.transform,
                //    d3.zoomIdentity.translate(
                //        this.diagramState.xPos,
                //        this.diagramState.yPos)
                //        .scale(this.diagramState.scale));


                // Restore this.zoom function
                this.svg.call(this.zoom)
                    .on("dblclick.zoom", null);

                this.selecting = false;
            }
        };
    }


    // Window resize handler
    resize(svg: d3Selection.Selection<SVGSVGElement, SVGDatum, any, any>) {
        const dims = this.getDimensionsFunc(this.targetDivName);
        this.width = dims.width;
        this.height = dims.height;
    }


    /**
     * IModelListenter.NodeRenamed
     * @param sender
     * @param pathAndName
     * @param newName
     */
    nodeRenamed(sender: Object, pathAndName: string, newName: string) {

        var newPathAndName = NodeObject.buildRenamedNodePathAndName(pathAndName, newName);

        // If renaming our sector
        if (pathAndName === this.context.sectorPathAndName) {
            this.context.sectorPathAndName = newPathAndName;

            this.nodes.forEach((curNode: INodeIcon) => {
                curNode.pathAndName = NodeObject.combinePathAndName(pathAndName, curNode.name);
            });
        }
        // Or, renaming a node in our sector
        else if (NodeObject.extractPath(pathAndName) === this.context.sectorPathAndName) {

            // Loop through and find the renamed node
            this.nodes.filter(node => node.pathAndName === pathAndName).forEach(curNode => {

                curNode.name = newName;
                curNode.pathAndName = newPathAndName;
                curNode.splitName = this.getSplitNodeName(newName, curNode.isShadowNode);
            });

            // Redraw doesn't pick up the name change so we have to transition it here
            const textToChange = d3.selectAll<SVGTextElement, INodeIcon>(".node-caption-text").filter(p => p.pathAndName == newPathAndName);
            this.getChangeTransition(textToChange)
                .text(d => d.splitName[0]);

        }
        else {

            // We might have renamed a shadow by the inline editor
            this.nodes.filter(p => p.pathAndName == pathAndName && p.isShadowNode)
                .forEach(curNode => {
                    const shadowName = this.modelValidation.buildShadowName(newName, curNode.path);
                    curNode.name = shadowName;
                    curNode.pathAndName = newPathAndName;
                    curNode.splitName = this.getSplitNodeName(shadowName, curNode.isShadowNode);
                });

            // Redraw doesn't pick up the name change so we have to transition it here
            const textToChange = d3.selectAll<SVGTextElement, INodeIcon>(".node-caption-text").filter(p => p.pathAndName == newPathAndName);
            this.getChangeTransition(textToChange)
                .text(d => d.splitName[0])
                .on("end", () => {
                    // Have to recreate the additional label lines because they get removed by changing the text
                    textToChange.each(function (icon) {
                        for (var i = 1; i < icon.splitName.length; i++) {
                            d3.select(this)
                                .append("tspan")
                                .attr("x", 22)
                                .attr("dy", "1.2em")
                                .text(icon.splitName[i])
                                .attr("class", "node-caption noselect node-caption-tspan");
                        }
                    });
                });
        }
    }

    private getChangeTransition<Element extends d3.BaseType, TDatum, HTMLElement extends d3.BaseType>(selection: d3.Selection<Element, TDatum, HTMLElement, any>):
        d3.Transition<Element, TDatum, HTMLElement, any> {
        return this
            .getDisappearTransition(selection)
            .transition()
            .duration(250)
            .style("opacity", 1)
    }

    private getFadeInTransition<Element extends d3.BaseType, TDatum, HTMLElement extends d3.BaseType>(selection: d3.Selection<Element, TDatum, HTMLElement, any>):
        d3.Transition<Element, TDatum, HTMLElement, any> {
        return selection
            .transition()
            .duration(250)
            .style("opacity", 1);
    }

    private getDisappearTransition<Element extends d3.BaseType, TDatum, HTMLElement extends d3.BaseType>(selection: d3.Selection<Element, TDatum, HTMLElement, any>):
        d3.Transition<Element, TDatum, HTMLElement, any> {
        return selection
            .transition()
            .duration(250)
            .style("opacity", 0);
    }


    /**
     * Nodes pasted handler
     * @param sender
     * @param nodePathAndNames
     */
    nodesPasted(sender: Object, targetPath: string, nodePathAndNames: string[], isCut: boolean) {
        // Do nothing here, easier just to redraw the whole diagram
    }


    /**
* IModelObserver.NodeCreated
* @param sender
* @param node
*/
    async nodeCreated(sender: Object, node: NodeObject, initData: any) {

        // Notification of a new node - if it belogs here,
        // Addit to our list and redraw.
        if (node.path === this.context.sectorPathAndName && !node.isFolder) {
            this.addNodeIcon(node.pathAndName,
                node.name,
                node.nodeType,
                initData.XPos,
                initData.YPos
            );

            // Don't select the node, or dragging it immediately won't work
            const newIcon = d3.select<SVGSymbolElement, INodeIcon>(`${this.targetDivName} .node-icon`)
                .filter(d => d.pathAndName === node.pathAndName) // Formerly used Id ?
                .classed("selected", false);

            //newIcon.classed("selected", true);

            // this.selectNode(newIcon);
            await this.reDraw()
                .then(() => this.nodeIconsTick(newIcon))
            // Doesn't work yet because blur is called, by refreshing the node tags editor apparently -
            //                .then(() => this.showInlineNodeNameEditor(createdIcon, false));
        }
    }


    // Because we are using promises to get shadow type names, we can only 
    // do this after we are sure everything is good
    async completeLayout(srcName: string,
        destName: string,
        needAutoLayout: boolean,
        commodity: string,
        units: string,
        properties: LinkProperty[]) {

        var sourceNodeIcon = this.findNode(srcName);
        var destNodeIcon = this.findNode(destName);

        if (destNodeIcon && sourceNodeIcon) {

            // Null kinkpoint, which will be rendered as the middle of the line
            var kinkPoint = { x: 0, y: 0 };
            // Now create the link control
            const newLinkControl = {
                source: sourceNodeIcon,
                target: destNodeIcon,
                commodity: commodity,
                units: units,
                properties: LinkProperty.propertiesToString(properties),
                sourcePathAndName: sourceNodeIcon.pathAndName,
                destPathAndName: destNodeIcon.pathAndName,
                getPoints: () => this.getLinkPoints(sourceNodeIcon, destNodeIcon, kinkPoint),
                kinkPoint: kinkPoint, //this.getDefaultKinkPointPos(sourceNodeIcon, destNodeIcon),
                id: NetworkDiagramBuilder.getRandomElementId()
            };

            // Add it to our internal array
            this.links.push(newLinkControl);

            // If a shadow was created, we will need to use the force here
            if (needAutoLayout) {
                this.startSimulation([sourceNodeIcon, destNodeIcon], false, this.buildConfig.autoCreateKinkPoints);
            }

            // Then redraw
            await this.reDraw()
                .then(() => {


                    // Now we have run the force, mark all positions as fixed
                    this.fixNodePositions();

                    // Update
                    this.tick();
                });

        };
    }
    static getRandomElementId = () => `id${Math.random().toFixed(10).slice(2)}`;


    /**
* IModelObserver.linkCreated
* @param sourceNode
* @param destNode
* @param commodity
* @param units
* @param properties
*/
    linkCreated(sourceNode: string,
        destNode: string,
        commodity: string,
        units: string,
        properties: LinkProperty[]): void {

        const srcPath = NodeObject.extractPath(sourceNode);
        const destPath = NodeObject.extractPath(destNode);


        if (srcPath === this.context.sectorPathAndName || destPath === this.context.sectorPathAndName) {

            const srcName = NodeObject.extractName(sourceNode);
            const destName = NodeObject.extractName(destNode);

            if (srcPath === destPath) {
                this.completeLayout(srcName, destName, false, commodity, units, properties);

            } else {
                // We have to create a shadow to show this link
                // Pass 0, 0 as the shadow coordinates, the auto-layout can fix it up
                // Shadowed node could be source or dest of the link

                let shadowName: string;
                let shadow: INodeIcon;

                if (srcPath !== this.context.sectorPathAndName) {
                    shadowName = this.modelValidation.buildShadowName(srcName, srcPath);
                    shadow = this.findNode(shadowName);
                    if (shadow) {
                        // Create the link control
                        this.completeLayout(shadowName, destName, false, commodity, units, properties);
                    }
                    else {
                        // No shadow found - create the shadow, then the link
                        this.modelActionsHandler.getNodeDataValue(sourceNode, "NodeTypeName")
                            .then((nodeTypeName: string) => {
                                this.addNodeIcon(
                                    sourceNode,
                                    shadowName,
                                    nodeTypeName, // Todo: get node type here
                                    0,
                                    0);

                                this.completeLayout(shadowName, destName, true, commodity, units, properties);

                            });

                    }

                } else if (destPath !== this.context.sectorPathAndName) {
                    shadowName = this.modelValidation.buildShadowName(destName, destPath);
                    shadow = this.findNode(shadowName);

                    if (shadow) {
                        // Create the link control
                        this.completeLayout(srcName, shadowName, false, commodity, units, properties);

                    } else {


                        // No shadow found - create the shadow, then the link
                        this.modelActionsHandler.getNodeDataValue(destNode, "NodeTypeName")
                            .then((nodeTypeName: string) => {

                                this.addNodeIcon(
                                    destNode,
                                    shadowName,
                                    nodeTypeName, // Todo: get type here 
                                    0,
                                    0);

                                this.completeLayout(srcName, shadowName, true, commodity, units, properties);

                            });
                    }
                }
            }

        }
    }

    /**
     * IModelObserver.nodesDeleted
     * @param sender
     * 
     * @param nodes
    */
    nodesDeleted(sender: Object, nodes: Array<NodeObject>) {

        // If we ourselve are deleted:
        var me = nodes.filter(node =>
            node.pathAndName === this.context.sectorPathAndName);
        if (me.length > 0) {
            d3.selectAll("svg > *").remove();
            return;
        }

        // First filter to nodes in our sector
        var nodesInSector = nodes.filter(node =>
            node.path === this.context.sectorPathAndName);

        // Then find the actual nodes deleted
        var iconsToDelete = new Array<INodeIcon>();

        nodesInSector.forEach(node => {
            const iconToDelete = this.nodes.find(p => p.pathAndName == node.pathAndName);
            //           var iconToDelete = this.findNode(node.name);
            if (iconToDelete)
                iconsToDelete.push(iconToDelete);
        });

        // Remove the icons
        iconsToDelete.forEach(theIcon => {
            this.removeNodeFromList(theIcon);
        });

        this.trimShadows();

        this.selectNode(null);
        this.reDraw();
        this.tick();

    }

    /**
     * Link deleted notification
     * @param sourceNode
     * @param destNode
     */
    linkDeleted(sourceNode: string, destNode: string) {

        // A link has been deleted, remove the associated link control
        const toDeleteIndex = this.links.findIndex(theLink => theLink.sourcePathAndName === sourceNode && theLink.destPathAndName === destNode);
        if (toDeleteIndex >= 0)
            this.links.splice(toDeleteIndex, 1);

        this.trimShadows();

        this.selectLink(null);
        this.reDraw();
        this.tick();

    }

    /**
     * Returns a node path and name, taking into account if it is a shadow or not
    * @param node
    */
    private getDiagramNodePathAndName(node: INodeIcon): string {
        return node.isShadowNode
            ? this.context.sectorPathAndName + "\\" + node.name
            : node.pathAndName;
    }


    /**
     * Link edited notification
     * @param srcNode
     * @param destNode
     * @param commodity
     * @param units
     * @param properties
     */
    linkEdited = (srcNode: string,
        destNode: string,
        commodity: string,
        units: string,
        properties: Array<LinkProperty>) => {

        for (let i = 0; i < this.links.length; i++) {
            const theLink = this.links[i];
            if ((theLink.source as INodeIcon).pathAndName === srcNode && (theLink.target as INodeIcon).pathAndName === destNode) {

                theLink.commodity = commodity;
                theLink.units = units;
                theLink.properties = LinkProperty.propertiesToString(properties);
                this.showLinkPopup(theLink);
            }

        }
    }

    zoomIn() {
        this.svg
            .transition()
            .call(this.zoom.scaleBy, 1.1)


        // Todo fix
        //    const curZoom = this.zoom.scale();
        //    const curPos = this.zoom.translate();
        //    const newZoom = curZoom + 0.1;
        //    this.zoom.scale(newZoom);
        //    this.container.transition().duration(500)
        //        .attr("transform", `translate(${curPos}) scale(${newZoom})`);
        //    this.diagramState.setScale(newZoom);
        //    this.tick();
    }

    zoomOut() {
        this.svg
            .transition()
            .call(this.zoom.scaleBy, 0.9)

        // Todo fix
        //    const curZoom = this.zoom.scale();
        //    const curPos = this.zoom.translate();
        //    const newZoom = curZoom - 0.1;
        //    this.zoom.scale(newZoom);
        //    this.container.transition().duration(500)
        //        .attr("transform", `translate(${curPos}) scale(${newZoom})`);
        //    this.diagramState.setScale(newZoom);
        //    this.tick();
    }


    /**
     * IModelObserver.NodesGrouped
     * @param sender
     * @param nodes
     * @param groupName
     */

    nodesGrouped(sender: Object, nodes: INodeObject[], groupName: string) {

        // Create grouped icon

        if (nodes) {

            // Take position of it from first node
            const firstNode = this.nodes.filter(p => p.pathAndName === nodes[0].pathAndName)[0];
            const position =
                { x: firstNode ? firstNode.x : 0, y: firstNode ? firstNode.y : 0 };

            const groupedIcon = this.addNodeIcon(
                NodeObject.combinePathAndName(this.context.sectorPathAndName, groupName),
                groupName,
                "GroupNode",
                position.x,
                position.y);

            var iconsToDelete = [];


            // Get rid of completely grouped links
            const groupedLinks = this.links.filter(link =>
                (nodes.filter(p => p.pathAndName === (link.target as INodeIcon).pathAndName).length > 0
                    && nodes.filter(p => p.pathAndName === (link.source as INodeIcon).pathAndName).length > 0)
                ||

                (nodes.filter(p => p.pathAndName === (link.target as INodeIcon).pathAndName).length > 0
                    && nodes.filter(p => p.pathAndName === (link.source as INodeIcon).pathAndName).length > 0)
            );

            for (let i = this.links.length - 1; i >= 0; i--)
                if (groupedLinks.indexOf(this.links[i]) >= 0)
                    this.links.splice(i, 1);


            // Loop ro
            nodes.forEach((curNode => {
                var name = NodeObject.extractName(curNode.pathAndName);

                var node = this.findNode(name);

                if (node) {
                    iconsToDelete.push(node);

                    // Now get rid of partially gone links
                    const nodeIndex = this.nodes.indexOf(node);
                    if (nodeIndex >= 0) {

                        const srcLinks =
                            this.links.filter(curLink => (curLink.source as INodeIcon).pathAndName === curNode.pathAndName);

                        srcLinks.forEach(curLink => curLink.source = groupedIcon);

                        const destLinks =
                            this.links.filter(curLink => (curLink.target as INodeIcon).pathAndName === curNode.pathAndName);
                        destLinks.forEach(curLink => curLink.target = groupedIcon);
                    }

                }

            }));

            // Remove the icons
            iconsToDelete.forEach(theIcon => {
                this.removeNodeFromList(theIcon);
            });

            this.trimShadows();

            this.selectNode(null);
            this.reDraw();
            this.tick();
        }
    }

    /**
     * IModelObserver.NodesUngrouped
     * @param sender
     * @param groupNodePathAndName
     */
    nodesUngrouped(sender: Object, groupNodePathAndName: string): Promise<any> {

        return Promise.resolve();
        //return $.Deferred().resolve();
        // Do nothing - diagram is refreshed when this happens.
    }

    fixNode(node: INodeIcon) {
        node.fx = node.x;
        node.fy = node.y;
        //node.fixed = true;
    }

    unFixNode(node: INodeIcon) {
        delete node.fx; // = null;
        delete node.fy; // = null;
        //node.fixed = false;
    }

    isNodeFixed(node: INodeIcon): boolean {
        const isUndef = (!node.fx && node.fx !== 0) || (!node.fy && node.fy !== 0);
        return !isUndef;
    }

    internalResetKinkPoints(links: ILinkControl[]): ILinkControl[] {

        var linksToReset = [];
        links.forEach(link => {
            if (NetworkDiagramBuilder.isValidCoordinate(link.kinkPoint)) {
                link.kinkPoint = { x: 0, y: 0 };
                linksToReset.push(link);
            }
        });
        return linksToReset;

        // Do not save here, because they might just be changed by a simulation start
    }

    resetKinkPoints(links?: ILinkControl[]) {

        if (!links)
            links = this.links;

        var linksToReset = this.internalResetKinkPoints(links);
        this.saveNodePositions([], linksToReset);

        this.getKinkPointElements()
            .transition()
            .duration(750)
            .attr("transform", d => this.getKinkTransform(d));

        this.getLinkControls()
            .transition()
            .duration(750)
            .attr("d", d => this.linkTransform(d));
    }

    relaxLayout(nodesToRelax: INodeIcon[]) {
        this.internalResetKinkPoints(this.links); // TODO This might not be all links

        if (nodesToRelax) {
            var nodesToFix = this.nodes.filter(x => nodesToRelax.indexOf(x) == -1);
            nodesToFix.forEach(this.fixNode);

            nodesToRelax.forEach(this.unFixNode);
            this.startSimulation(nodesToFix, true, this.buildConfig.autoCreateKinkPoints);

        }
        else {
            this.nodes.forEach(this.unFixNode);
            this.startSimulation(this.nodes, true, this.buildConfig.autoCreateKinkPoints);
        }

    }

    autoLayoutAllNodes() {
        //        this.nodes.forEach(this.unFixNode);
        //      this.relaxLayout();

        this.nodes.forEach(this.unFixNode);
        this.internalResetKinkPoints(this.links);
        this.startSimulation(this.nodes, false, this.buildConfig.autoCreateKinkPoints);
        //this.internalSnapNodesToGrid(this.buildConfig.snapNodesToGrid, this.buildConfig.snapGridSize);
        this.saveNodeIconPositions(this.nodes.filter(p => !this.isNodeFixed(p)), this.links);
        this.nodes.forEach(this.fixNode);
        //        this.centerDiagram(false);
        //        this.tick();
    }

    //applyCollisionDetection(nodes: INodeIcon[]) {
    //    var q = d3.quadtree(nodes),
    //        i = 0,
    //        n = nodes.length;

    //    while (++i < n) q.visit(this.collide(nodes[i]));

    //}

    //collide(node: INodeIcon) {
    //    const radius = 40;

    //    var r = radius, // node. radius + 16,
    //        nx1 = node.x - r,
    //        nx2 = node.x + r,
    //        ny1 = node.y - r,
    //        ny2 = node.y + r;
    //    return (quad, x1, y1, x2, y2) => {
    //        if (quad.point && (quad.point !== node)) {
    //            var x = node.x - quad.point.x,
    //                y = node.y - quad.point.y,
    //                l = Math.sqrt(x * x + y * y),
    //                r = radius /*node.radius*/ + quad.point.radius;
    //            if (l < r) {
    //                l = (l - r) / l * .5;
    //                node.x -= x *= l;
    //                node.y -= y *= l;
    //                quad.point.x += x;
    //                quad.point.y += y;
    //            }
    //        }
    //        return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
    //    };
    //}

    saveNodeIconPositions(nodes: Array<INodeIcon>, links: Array<ILinkControl>) {
        const positions = nodes.map(
            node => { return { nodePathAndName: this.getDiagramNodePathAndName(node), xPos: node.x, yPos: node.y } });

        this.saveNodePositions(positions, links);

    }

    async saveNodePositions(newPositions: Array<INodeDiagramPosition>, links: Array<ILinkControl>) {
        this.modelActionsHandler.setNodePositions(newPositions)
            .then(() => {

                if (links.length > 0) {

                    const kinkPoints = links.map(link => {
                        return {
                            parentPathAndName: this.context.sectorPathAndName,
                            sourceNodePathAndName: link.sourcePathAndName,
                            destNodePathAndName: link.destPathAndName,
                            point: { x: link.kinkPoint.x, y: link.kinkPoint.y }
                        }

                    });

                    this.modelActionsHandler.setKinkPoints(kinkPoints);

                }
            });
    }


    internalSnapNodesToGrid(snapNodesToGrid: boolean, snapGridSize?: number) {
        if (snapNodesToGrid) {
            if (snapGridSize === undefined)
                snapGridSize = 50;

            this.nodes.forEach(curNode => {
                var nx = Math.round(curNode.x / snapGridSize) * snapGridSize;
                var ny = Math.round(curNode.y / snapGridSize) * snapGridSize;

                curNode.x = nx;
                curNode.y = ny;
                this.fixNode(curNode);
            });
        }

    }
    snapNodesToGrid(snapNodesToGrid: boolean, snapGridSize?: number) {
        this.internalSnapNodesToGrid(true, snapGridSize);
        this.tick();
    }

    getKinkNodes(nodesToLayout: INodeIcon[], linksToLayout: ILinkControl[]) {

        const result = linksToLayout.map<IKinkNodeIcon>(link => {
            //      var points = this.getLinkPoints(link.source, link.target, link.kinkPoint);

            let xy = { x: 0, y: 0 };
            if (link.kinkPoint)
                xy = link.kinkPoint;

            if (nodesToLayout.indexOf(link.source) >= 0 && nodesToLayout.indexOf(link.target) >= 0)
                return {
                    fqmId: "",
                    isFolder: false,
                    isShadowNode: false,
                    name: "",
                    nodeType: "kink",
                    path: link.destPathAndName, // Use this as placeholder
                    pathAndName: link.sourcePathAndName, // And this
                    selectionTime: 0,
                    splitName: [],
                    index: -1,
                    x: xy.x,
                    y: xy.y,
                    link: link

                }
            else
                return null;
        });

        // Get rid of nulls
        return result.filter(x => x);
    }

    getCaptionNodes(nodes: INodeIcon[]): ICaptionNodeIcon[] {
        const result = nodes.map(
            node => ({
                node: node,
                name: `caption ${node.name}`,
                x: node.x + 22,
                y: node.y - 8,
                splitName: [],
                isShadowNode: false,
                selectionTime: 0,
                pathAndName: `caption ${node.name}`,
                nodeType: "caption",
                path: `caption ${node.name}`,
                isFolder: false,
                fqmId: `caption ${node.name}`,
                width: Math.max(...node.splitName.map(p => p.length)) * 5.5, // Approximation of name length
                height: node.splitName.length * 18 // Approximation of name height
            })
        );
        return result;
    }

    getCaptionLinks(captionNodes: ICaptionNodeIcon[]): ICaptionNodeLink[] {
        return captionNodes.map<ICaptionNodeLink>(caption => {
            return {
                commodity: "",
                destPathAndName: "",
                id: "",
                kinkPoint: { x: 0, y: 0 },
                properties: "",
                sourcePathAndName: "",
                units: "",
                source: caption.node,
                target: caption,
                captionNode: caption,

            }
        });
    }


    startSimulation(nodesToLayout: INodeIcon[], keepRunning: boolean, autoCreateKinkPoints: boolean) {

        if (nodesToLayout.length === 0)
            nodesToLayout = this.nodes;

        if (nodesToLayout.find(node => !node.fx && !node.fy) == null)
            return;

        // All nodes need to be in force, but only unfixed nodes will be affected
        let nodesForForce = this.nodes; // nodesToLayout;
        let kinkNodes = [];

        let linksForForce = [];

        if (this.buildConfig.forceSimulationConfig.createFalseNodesForSimulation) {

            if (this.buildConfig.autoCreateKinkPoints)
                // Only create kink nodes for nodes that may be moved
                kinkNodes = this.getKinkNodes(nodesToLayout, this.links);

            // Create links in and out of the kink nodes
            const kinkInputLinks = this.getKinkNodeInputLinks(kinkNodes);
            const kinkOutputLinks = this.getKinkNodeOutputLinks(kinkNodes);

            // Get nodes based on the caption of all nodes
            const captionNodes = this.getCaptionNodes(this.nodes);

            // Final node list
            nodesForForce = nodesForForce
                .concat(captionNodes)
                .concat(kinkNodes);

            // Final link list
            linksForForce =
                kinkOutputLinks
                    .concat(kinkInputLinks)
                    .concat(this.getCaptionLinks(captionNodes));

        }
        else
            linksForForce = this.links.filter(
                link => nodesToLayout.indexOf(link.source) >= 0 &&
                    nodesToLayout.indexOf(link.target) >= 0)


        this.force
            .nodes(nodesForForce)
            .force("link", d3.forceLink(linksForForce).distance(5))
            .alpha(0.9)
            .velocityDecay(0.8);

        var thisBuilder = this;

        if (keepRunning)
            this.force.restart()
                .on("end", function () {
                    thisBuilder.onForceEnd(kinkNodes);
                    this.tick();

                });
        else {
            this.force.tick(300);
            this.onForceEnd(kinkNodes);

        }
    }

    private onForceEnd(kinkNodes: IKinkNodeIcon[]) {
        // if (this.buildConfig.createFalseNodesForSimulation) 
        {

            // Now put the changed kinkpoints points on to the links
            kinkNodes.forEach(kink => {
                // Kinkpoint is stored as a point relative to src
                // So we have to de-normalize the point
                const relativeKp = {
                    x: kink.x - kink.link.source.x, y: kink.y - kink.link.source.y
                };

                kink.link.kinkPoint = relativeKp;
            });
        }

        this.getNodeIcons()
            .transition()
            .duration(500)
            .attr("transform", this.nodeTransform);

        this.getLinkControls()
            .transition()
            .duration(500)
            .attr("d", d => this.linkTransform(d)); //  this.pathTransform);

        this.getNodeCaptionElements()
            .transition()
            .duration(500)
            .attr("transform", this.nodeTransform);

        this.getKinkPointElements()
            .transition()
            .duration(500)
            .attr("transform", d => this.getKinkTransform(d));
    }

    private getKinkNodeOutputLinks(kinkNodes: IKinkNodeIcon[]) {
        return kinkNodes.map<ILinkControl>(kink => {
            return {
                commodity: "",
                destPathAndName: "",
                id: "",
                kinkPoint: { x: 0, y: 0 },
                properties: "",
                sourcePathAndName: "",
                units: "",
                source: kink,
                target: kink.link.target
            };
        });
    }

    private getKinkNodeInputLinks(kinkNodes: IKinkNodeIcon[]) {
        return kinkNodes.map<ILinkControl>(kink => {
            return {
                commodity: "",
                destPathAndName: "",
                id: "",
                kinkPoint: { x: 0, y: 0 },
                properties: "",
                sourcePathAndName: "",
                units: "",
                source: kink.link.source,
                target: kink
            };
        });
    }

    getD3SymbolFromString(shape: SymbolShapeString): d3.SymbolType {

        const symbolMap: Record<string, d3.SymbolType> = {
            "circle": d3.symbolCircle,
            "square": d3.symbolSquare,
            "diamond": d3.symbolDiamond,
            "triangle-up": d3.symbolTriangle,
            "triangle-down": d3.symbolTriangle,

        };

        return symbolMap[shape];

    };


    distributeNodes(mode: NodeDistributionMode, distanceX, distanceY: any) {

        // Moronic casting because input can change number to a string
        let distanceValX: number = distanceX;
        if (typeof distanceX === "string")
            distanceValX = +distanceX;

        let distanceValY: number = distanceY;
        if (typeof distanceY === "string")
            distanceValY = +distanceY;

        // For some reason calling d3 sort on the selectedNodes seems to re-order the nodes themselves ?
        const selectedNodes: d3.Selection<SVGSymbolElement, INodeIcon, any, any> = d3.selectAll(".node-icon.selected");
        const mappedData = selectedNodes.data().map(p => p);

        const sortedNodes = mappedData.sort((a, b) => a.selectionTime - b.selectionTime);
        var nodeData = selectedNodes.data();

        // Refresh links
        var links = this.container.selectAll<SVGLineElement, ILinkControl>("path.link")
            .filter(link => {
                let isSelected = nodeData.find(node => link.target === node || link.source == node);
                return isSelected != null;
            });

        this.internalResetKinkPoints(links.data());

        let targetY, targetX = 0;
        sortedNodes.forEach((icon, index) => {

            switch (mode) {

                default: // obliquely
                    if (index == 0) {
                        targetX = icon.x;
                        targetY = icon.y;
                    }
                    else {
                        targetX += distanceValX;
                        targetY += distanceValY;
                    }
                    break;
            }

            icon.y = targetY;
            icon.x = targetX;


        });

        // Transition nodes
        selectedNodes
            .transition()
            .duration(750)
            .attr("transform", (d: INodeIcon) =>
                `translate(${d.x},${d.y})`)

        // Transition links
        links
            .transition()
            .duration(750)
            .attr("d", d => this.linkTransform(d));

        // Transition captions
        const text = this.container.selectAll<SVGTextElement, INodeIcon>(".node-caption")
            .filter(theText => {
                const isSelected = nodeData.find(node => theText === node);
                return isSelected != null;
            });

        text
            .transition()
            .duration(750)
            .attr("transform", this.nodeTransform);

        // Transition kink points
        const kinkElements = this.container.selectAll<SVGSymbolElement, ILinkControl>(".kinkpoint")
            .filter(kp => nodeData.indexOf(kp.source) >= 0 || nodeData.indexOf(kp.target) >= 0);

        kinkElements
            .transition()
            .duration(750)
            .attr("transform", d => this.getKinkTransform(d));


        // Todo reset 
        this.saveNodeIconPositions(nodeData, kinkElements.data());
    }

    clear(options: { removePalette: boolean; }) {
        d3.selectAll("svg > *").remove();
    }

}

function getNodeForceRadius(d: d3.SimulationNodeDatum): number {

    var icon = (d as INodeIcon);

    if (icon.nodeType == "caption") {
        const captionNode = d as ICaptionNodeIcon;
        const result = captionNode.width / 7;
        return result;
    }
    else
        if (icon.nodeType == "kink") {
            return 20;
        }

    return 10;

}



// WEBPACK FOOTER //
// ./ClientApp/scripts/ah.mb.ui.network-diagram.ts